import { Storage } from '../lib/storage';
import React, { useState, useEffect, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { ShieldCheck, X, PlusCircle, Trash2, History, FileText, RefreshCw, Edit3, Wrench, Copy, Crown, Droplet, Gauge, ChevronLeft, ChevronRight, Download, CheckCircle, AlertCircle, MapPin, Shield, Zap, Bike, CarFront } from 'lucide-react';
import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import { toJpeg } from 'html-to-image';
import autoTable from 'jspdf-autotable';
import { useCountry } from '../hooks/useCountry';

export default function ExpedienteScreen({ themeClass = 'bg-[#050505]', onOpenMembresia, onOpenTaller, role }: { themeClass?: string, onOpenMembresia?: () => void, onOpenTaller?: () => void, role?: string }) {
  const { config } = useCountry();
  const [mostrarQR, setMostrarQR] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [hash, setHash] = useState('');
  const [generando, setGenerando] = useState(false);
  const [motoActiva, setMotoActiva] = useState('');
  const [userNameAlias, setUserNameAlias] = useState('');
  const [userNameReal, setUserNameReal] = useState('');
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [motosReales, setMotosReales] = useState<any[]>([]);
  
  // MODO ENFOQUE GARAJE (Contextual UI) - Determina si mostrar el tablero de gestión o centrarse en registro
  const esModoVinculacion = !motoActiva || motoActiva === 'NUEVA_ACCION' || motosReales.length === 0;
  const [cargandoMotos, setCargandoMotos] = useState(false);

  const [triggerSync, setTriggerSync] = useState(0);
  const [isOro, setIsOro] = useState(false);
  const [toastPikis, setToastPikis] = useState<{msg: string, tipo: 'success' | 'error' | 'info'} | null>(null);
  const [dialogoComp, setDialogoComp] = useState<{titulo: string, msg: string, accion: () => void} | null>(null);
  const [idSaltoPendiente, setIdSaltoPendiente] = useState<string | null>(null);

  const mostrarAviso = (msg: string, tipo: 'success' | 'error' | 'info' = 'info') => {
    setToastPikis({ msg, tipo });
    setTimeout(() => setToastPikis(null), tipo === 'error' ? 5000 : 3000);
  };



  useEffect(() => {
    if (idSaltoPendiente && motosReales.length > 0) {
      setTimeout(() => {
        const targetElement = document.getElementById(`moto-card-${idSaltoPendiente}`);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          
          // Actualizar el índice visual basado en la posición en el array
          const idx = motosReales.findIndex(m => m.id.toString() === idSaltoPendiente);
          if (idx !== -1) setActiveCardIndex(idx);
          
          setIdSaltoPendiente(null);
        }
      }, 600);
    }
  }, [motosReales, idSaltoPendiente]);

  useEffect(() => {
    let knownMotoIds = new Set<string>();
    let subChannel: any = null;

    // Detect payment return from MP
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('pago') === 'exito') {
      onOpenMembresia?.();
      mostrarAviso('¡Felicidades! Se ha activado tu Membresía SCORE ORO.', 'success');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('pago') === 'rechazado') {
      mostrarAviso('Tu pago ha sido rechazado o cancelado. Inténtalo de nuevo.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const fetchMotos = async (showLoader = true) => {
      if (showLoader) setCargandoMotos(true);

      // --- IDENTIDAD SINCRÓNICA ---
      let userId = Storage.get('motopikis_user_id');
      const userStr = Storage.get('motopikis_user');
      if (!userId && userStr && userStr.startsWith('{')) {
        try { userId = JSON.parse(userStr).id; } catch (e) {}
      }

      // --- 1. CARGA OPTIMISTA (DESDE BÓVEDA LOCAL) ---
      try {
        const cachedMotos = Storage.get('motopikis_cache_motos');
        const cacheOwner = Storage.get('motopikis_cache_owner');
        
        // Cargamos si hay caché y coincide con el usuario (o si no hay ID aún para validar)
        if (cachedMotos && (!userId || userId === cacheOwner)) {
          const data = JSON.parse(cachedMotos);
          setMotosReales(data);
          
          const cachedMetricas = Storage.get('motopikis_metricas');
          if (cachedMetricas) setMetricasMotos(JSON.parse(cachedMetricas));
          
          const cachedHistorial = Storage.get('motopikis_historial_gas');
          if (cachedHistorial) setHistorialGas(JSON.parse(cachedHistorial));
          
          if (!motoActiva && data.length > 0) setMotoActiva(data[0].id.toString());
          if (showLoader) setCargandoMotos(false);
          console.log("OFFLINE-FIRST: Garaje restaurado desde Bóveda Local.");
        }
      } catch (e) {
        console.warn("Error en carga optimista:", e);
      }

      // --- 2. SINCRONIZACIÓN CON LA NUBE ---
      // Si no tenemos internet, no intentamos nada más para evitar bloqueos
      if (!window.navigator.onLine) {
         if (showLoader) setCargandoMotos(false);
         return;
      }

      try {
        if (!userId) {
          const { data: { session } } = await supabase.auth.getSession();
          userId = session?.user?.id;
          if (userId) Storage.set('motopikis_user_id', userId as string);
        }

        if (userId) {
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('TIMEOUT_EXCEDIDO')), 12000));

          // Obtener Perfil Usuario con Vencimiento
          const userFetch = supabase.from('usuarios').select('alias, nombre, es_oro, vencimiento_oro').eq('id', userId).single();
          const userRes = await Promise.race([userFetch, timeoutPromise]).catch(() => null) as any;
          if (userRes?.data) {
            const ud = userRes.data;
            
            // Lógica de Validación de Tiempo SCORE ORO
            let activeOro = !!ud.es_oro;
            if (ud.vencimiento_oro) {
              const vence = new Date(ud.vencimiento_oro);
              if (vence < new Date()) {
                activeOro = false;
                console.log("Suscripción SCORE ORO expirada.");
              }
            }
            
            setIsOro(activeOro);
            setUserNameReal(ud.nombre || '');
            const finalAlias = (ud.alias || ud.nombre || '').toLowerCase().split(' ').map((w: any) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            setUserNameAlias(finalAlias);
          }

          // Obtener Vehículos limpios sin join para evitar 400 Bad Request de PostgREST
          const vehiculosFetch = supabase.from('vehiculos').select('*').eq('usuario_id', userId);
          let vRes = await Promise.race([vehiculosFetch, timeoutPromise]).catch(() => null) as any;
          if (!vRes?.data) {
             vRes = await vehiculosFetch;
          }
          
          if (vRes?.data) {
            const finalData = vRes.data;
            const vIds = finalData.map((m: any) => String(m.id));
            
            if (vIds.length > 0) {
              const { data: legData } = await supabase.from('documentos_legales').select('*').in('vehiculo_id', vIds);
              if (legData) {
                finalData.forEach((m: any) => {
                  m.documentos_legales = legData.filter((l: any) => String(l.vehiculo_id) === String(m.id));
                });
              }

              const { data: cd } = await supabase.from('consumibles_moto').select('*').in('vehiculo_id', vIds);
              if (cd) {
                finalData.forEach((m: any) => {
                  m.consumibles_moto = cd.filter((c: any) => String(c.vehiculo_id) === String(m.id));
                });
              }
            }

            setMotosReales(finalData);
            Storage.set('motopikis_cache_motos', JSON.stringify(finalData));
            Storage.set('motopikis_cache_owner', userId);
            
            if (finalData.length > 0) {
               const currentActiva = finalData.find((m: any) => m.id.toString() === motoActiva);
               if (!currentActiva) {
                  setMotoActiva(finalData[0].id.toString());
               }
            } else {
               setMotoActiva('');
            }
            
            const newMetricas: Record<string, { km: number; rendimiento: number }> = {};
            const newHistorial: Record<string, any[]> = {};

            finalData.forEach((m: any) => {
              newMetricas[m.id.toString()] = { km: m.km_actual || 0, rendimiento: 0 };
            });

            const { data: hData } = await supabase.from('historial_combustible').select('*').in('vehiculo_id', vIds).order('fecha_repostaje', { ascending: false });
            if (hData) {
              hData.forEach((record: any) => {
                const vIdStr = record.vehiculo_id.toString();
                if (!newHistorial[vIdStr]) newHistorial[vIdStr] = [];
                newHistorial[vIdStr].push({
                  id_db: record.id,
                  fecha: new Date(record.fecha_repostaje).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }),
                  valor: formatMiles(record.valor_pagado?.toString() || '0'),
                  km: record.km_odometro || 0,
                  rendimiento: 0,
                  galones: Number(record.galones) || 0
                });
              });

              Object.keys(newHistorial).forEach(vid => {
                const logs = newHistorial[vid];
                for (let i = 0; i < logs.length - 1; i++) {
                  if (logs[i].galones > 0 && logs[i].km > logs[i+1].km) {
                    logs[i].rendimiento = Math.round((logs[i].km - logs[i+1].km) / logs[i].galones);
                  }
                }
                if (logs.length > 0) newMetricas[vid].rendimiento = logs[0].rendimiento;
              });
            }

            setMetricasMotos(newMetricas);
            setHistorialGas(newHistorial);
            Storage.set('motopikis_metricas', JSON.stringify(newMetricas));
            Storage.set('motopikis_historial_gas', JSON.stringify(newHistorial));
            
            knownMotoIds = new Set(vIds);
            if (!motoActiva && finalData.length > 0) setMotoActiva(finalData[0].id.toString());
          }
        }
      } catch (err) {
        console.warn("Sincronización silenciosa fallida.");
      }
      if (showLoader) setCargandoMotos(false);
    };

    const handleNetRefersh = () => {
       fetchMotos(false);
    };
    window.addEventListener('online', handleNetRefersh);

    fetchMotos().then(() => {
       // ... existing channel logic ...
      subChannel = supabase.channel('schema-db-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'vehiculos' }, () => fetchMotos(false))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'documentos_legales' }, () => fetchMotos(false))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'consumibles_moto' }, () => fetchMotos(false))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'usuarios' }, () => fetchMotos(false))
        .subscribe();
    });

    return () => { 
      if (subChannel) supabase.removeChannel(subChannel); 
      window.removeEventListener('online', handleNetRefersh);
    };
  }, [triggerSync, role, Storage.get('motopikis_user_id')]);

  const handleEliminarMoto = async (motoId: string, placa?: string) => {
    setDialogoComp({
      titulo: 'ELIMINAR VEHÍCULO',
      msg: '¿Seguro que deseas eliminar este vehículo permanentemente de tu Garaje?',
      accion: async () => {
        try {
          let userId = Storage.get('motopikis_user_id');
          if (!userId) {
            const userStr = Storage.get('motopikis_user');
            if (userStr && userStr.startsWith('{')) {
              try { userId = JSON.parse(userStr).id; Storage.set('motopikis_user_id', userId as string); } catch(e) {}
            }
          }
          if (!userId) {
             const { data: { session } } = await supabase.auth.getSession();
             if (session?.user?.id) { userId = session.user.id; Storage.set('motopikis_user_id', userId); }
          }
          
          if (userId && motoId) {
            console.log("DEBUG: Intentando borrar motoId:", motoId, "UId:", userId);
            const { error, count, status } = await supabase
              .from('vehiculos')
              .delete({ count: 'exact' })
              .eq('id', motoId)
              .eq('usuario_id', userId);

            console.log("DEBUG: Respuesta Servidor - Status:", status, "Error:", error, "Filas Afectadas:", count);

            if (error) {
              console.error("Error al eliminar vehículo:", error);
              mostrarAviso('Error servidor: ' + error.message, 'error');
              setDialogoComp(null);
              return;
            }

            if (count === 0) {
              console.warn("DEBUG: No se borró nada. Probablemente el ID o el Usuario no coinciden.");
              // Intento de rescate: Borrar solo por ID ya que estamos en modo abierto
              const { error: error2, count: count2 } = await supabase.from('vehiculos').delete({ count: 'exact' }).eq('id', motoId);
              console.log("DEBUG: Intento 2 (Solo ID) - Filas:", count2, "Error:", error2);
              
              if (count2 === 0) {
                 mostrarAviso('Error: Registro no encontrado en la nube.', 'error');
                 setDialogoComp(null);
                 return;
              }
            }
          }
          setMotosReales((prev) => {
            const restantes = prev.filter(m => m.id.toString() !== motoId);
            if (motoActiva === motoId) {
              setMotoActiva(restantes.length > 0 ? restantes[0].id.toString() : '');
            }
            return restantes;
          });
          mostrarAviso('Vehículo eliminado permanentemente.', 'success');
        } catch(e) { console.error(e) }
        setDialogoComp(null);
      }
    });
  };
  const [pestañaActiva, setPestañaActiva] = useState('');
  const [mostrarModalKm, setMostrarModalKm] = useState(false);
  const [nuevoKm, setNuevoKm] = useState('');
  const [mostrarModalGas, setMostrarModalGas] = useState(false);
  const [kmGas, setKmGas] = useState('');
  const [valorGas, setValorGas] = useState('');
  const [galonesGas, setGalonesGas] = useState('');
  const [mostrarModalLegales, setMostrarModalLegales] = useState(false);
  const [tipoModalLegales, setTipoModalLegales] = useState('');
  const [fechaLegal, setFechaLegal] = useState('');
  const [mostrandoFormMulta, setMostrandoFormMulta] = useState(false);
  const [nombreOtroDoc, setNombreOtroDoc] = useState('');
  const [mostrarModalConsumible, setMostrarModalConsumible] = useState(false);
  const [nuevoConsumibleNombre, setNuevoConsumibleNombre] = useState('');
  const [nuevoConsumibleDuracion, setNuevoConsumibleDuracion] = useState('');
  const [nuevoConsumibleValor, setNuevoConsumibleValor] = useState('');
  const [nuevoConsumibleManoObra, setNuevoConsumibleManoObra] = useState('');
  const [editandoConsumibleId, setEditandoConsumibleId] = useState<string | null>(null);

  const [mostrarModalGarantia, setMostrarModalGarantia] = useState(false);
  const [nuevaGarantiaTitulo, setNuevaGarantiaTitulo] = useState('');
  const [nuevaGarantiaValor, setNuevaGarantiaValor] = useState('');
  const [nuevaGarantiaFecha, setNuevaGarantiaFecha] = useState('');
  const [editandoGarantiaId, setEditandoGarantiaId] = useState<string | null>(null);
  const [mostrarModalPremium, setMostrarModalPremium] = useState(false);
  const [toastGas, setToastGas] = useState<{ show: boolean, msg: string, type: 'success' | 'error' }>({ show: false, msg: '', type: 'success' });
  
  const [formVIP, setFormVIP] = useState<{
    isOpen: boolean;
    titulo: string;
    targetMoto: 'mia' | 'otra' | null;
    placa: string;
    cedula: string;
    nombre: string;
  }>({
    isOpen: false,
    titulo: '',
    targetMoto: null,
    placa: '',
    cedula: '',
    nombre: ''
  });
  const formatTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
  };

  const handleEliminarDocumento = async (tipo_documento: string, db_id?: string) => {
    setDialogoComp({
      titulo: 'ELIMINAR DOCUMENTO',
      msg: `¿Seguro que deseas eliminar el documento ${tipo_documento}?`,
      accion: async () => {
        try {
          if (db_id) {
            await supabase.from('documentos_legales').delete().eq('id', db_id);
          } else {
            await supabase.from('documentos_legales').delete().eq('vehiculo_id', motoActiva).eq('tipo_documento', tipo_documento);
          }
          setTriggerSync(s => s + 1);
          mostrarAviso('Documento eliminado.', 'success');
        } catch(e: any) {
          mostrarAviso("Error al eliminar documento: " + e.message, 'error');
        }
        setDialogoComp(null);
      }
    });
  };


  const cerrarModalConsumibles = () => {
    setMostrarModalConsumible(false);
    setEditandoConsumibleId(null);
    setNuevoConsumibleNombre('');
    setNuevoConsumibleDuracion('');
    setNuevoConsumibleValor('');
    setNuevoConsumibleManoObra('');
  };

  const cerrarModalGarantia = () => {
    setMostrarModalGarantia(false);
    setEditandoGarantiaId(null);
    setNuevaGarantiaTitulo('');
    setNuevaGarantiaValor('');
    setNuevaGarantiaFecha('');
  };

  const abrirEdicionGarantia = (gar: any) => {
    setNuevaGarantiaTitulo(gar.title);
    setNuevaGarantiaFecha(gar.text.replace('Fecha: ','').trim());
    setNuevaGarantiaValor(gar.valorInfo.replace(/\D/g, ''));
    setEditandoGarantiaId(gar.id);
    setMostrarModalGarantia(true);
  };

  const handleGuardarGarantia = async () => {
    if (!nuevaGarantiaTitulo || !nuevaGarantiaFecha) {
       mostrarAviso("El título y la fecha son obligatorios", 'error');
       return;
    }
    try {
      const rawValor = nuevaGarantiaValor.replace(/\D/g, '');
      const payload = `GARANTIA_||_${nuevaGarantiaTitulo}_||_${rawValor || '0'}`;
      
      if (editandoGarantiaId) {
        const { error } = await supabase.from('documentos_legales').update({
          tipo_documento: payload,
          fecha_vencimiento: nuevaGarantiaFecha
        }).eq('id', editandoGarantiaId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('documentos_legales').insert([{
          vehiculo_id: motoActiva,
          tipo_documento: payload,
          fecha_vencimiento: nuevaGarantiaFecha,
          estado: 'PENDIENTE'
        }]);
        if (error) throw error;
      }
      
      cerrarModalGarantia();
      setTriggerSync(s => s + 1);
    } catch(e: any) { mostrarAviso("Error: " + e.message, 'error'); }
  };

  const handleToggleGarantia = async (id: string, current: boolean) => {
    try {
      const { error } = await supabase.from('documentos_legales').update({ estado: current ? 'PENDIENTE' : 'HECHO' }).eq('id', id);
      if (error) throw error;
      setTriggerSync(s => s + 1);
    } catch(e: any) { mostrarAviso("Error: " + e.message, 'error'); }
  };

  const handleGuardarConsumible = async () => {
    if (!nuevoConsumibleNombre || !nuevoConsumibleDuracion) {
       mostrarAviso("Completa los datos del repuesto", 'error');
       return;
    }
    try {
      const duracionParsed = parseInt(nuevoConsumibleDuracion.replace(/\D/g, ''), 10);
      const kmActual = metricasMotos[motoActiva]?.km || 0;
      
      const payload = {
        nombre_repuesto: nuevoConsumibleNombre,
        km_instalacion: kmActual,
        km_duracion: duracionParsed,
        fecha_instalacion: new Date().toISOString().split('T')[0],
        valor_repuesto: parseInt(nuevoConsumibleValor.replace(/\D/g, '')) || 0,
        costo_instalacion: parseInt(nuevoConsumibleManoObra.replace(/\D/g, '')) || 0
      };

      if (editandoConsumibleId) {
        const { error } = await supabase.from('consumibles_moto').update(payload).eq('id', editandoConsumibleId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('consumibles_moto').insert([{
           ...payload,
           vehiculo_id: motoActiva
        }]);
        if (error) throw error;
      }
      
      cerrarModalConsumibles();
      setTriggerSync(s => s + 1);
    } catch(e: any) { 
      mostrarAviso("Error guardando repuesto en BD: " + e.message, 'error'); 
    }
  };

  const handleEliminarConsumible = async (id: string) => {
    setDialogoComp({
      titulo: 'ELIMINAR INSUMO',
      msg: "¿Seguro que deseas eliminar este repuesto del historial del vehículo?",
      accion: async () => {
        try {
          const { error } = await supabase.from('consumibles_moto').delete().eq('id', id);
          if (error) throw error;
          setTriggerSync(s => s + 1);
          mostrarAviso('Insumo eliminado.', 'success');
        } catch(e: any) { mostrarAviso("Error al eliminar: " + e.message, 'error'); }
        setDialogoComp(null);
      }
    });
  };

  const handleRenovarConsumible = (cons: any) => {
    setEditandoConsumibleId(cons.id);
    setNuevoConsumibleNombre(cons.name || '');
    setNuevoConsumibleDuracion(cons.km_duracion ? formatMiles(cons.km_duracion.toString()) : '');
    setNuevoConsumibleValor(cons.valor ? formatMiles(cons.valor.toString()) : '');
    setNuevoConsumibleManoObra(cons.instalacion ? formatMiles(cons.instalacion.toString()) : '');
    setMostrarModalConsumible(true);
  };

  const handleGuardarDocumento = async () => {
    if (!fechaLegal) {
       mostrarAviso("Ingresa la fecha de vencimiento", 'error');
       return;
    }
    
    const mapType: any = {
      'soat': 'SOAT',
      'tecno': 'TECNOMECANICA',
      'impuesto': 'IMPUESTO',
      'poliza': 'POLIZA'
    };
    const docType = tipoModalLegales === 'OTRO' ? (formatTitleCase(nombreOtroDoc) || 'Otro') : (mapType[tipoModalLegales] || tipoModalLegales);
    
    try {
      await supabase.from('documentos_legales')
         .delete()
         .eq('vehiculo_id', motoActiva)
         .eq('tipo_documento', docType);

      await supabase.from('documentos_legales').insert([{
        vehiculo_id: motoActiva,
        tipo_documento: docType,
        fecha_vencimiento: fechaLegal,
        estado: 'ACTIVO'
      }]);
      mostrarAviso("¡Documento sincronizado exitosamente!", 'success');
      setMostrarModalLegales(false);
      setFechaLegal('');
      setNombreOtroDoc('');
      
      setTriggerSync(s => s + 1);
    } catch(e: any) {
      mostrarAviso("Error: " + e.message, 'error');
    }
  };

  const handleGuardarSimit = async (estadoStr: string) => {
    try {
      const hoy = new Date().toISOString().split('T')[0];
      await supabase.from('documentos_legales')
         .delete()
         .eq('vehiculo_id', motoActiva)
         .eq('tipo_documento', 'SIMIT');

      await supabase.from('documentos_legales').insert([{
        vehiculo_id: motoActiva,
        tipo_documento: 'SIMIT',
        fecha_vencimiento: hoy,
        estado: estadoStr
      }]);
      mostrarAviso("¡Estado SIMIT actualizado!", 'success');
      setMostrarModalLegales(false);
      setMostrandoFormMulta(false);
      
      setTriggerSync(s => s + 1);
    } catch(e: any) {
      mostrarAviso("Error SIMIT: " + e.message, 'error');
    }
  };
  const [metricasMotos, setMetricasMotos] = useState<Record<string, { km: number, rendimiento: number }>>(() => {
    try {
      const saved = Storage.get('motopikis_metricas');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  
  const [mostrarHistorialGas, setMostrarHistorialGas] = useState(false);
  const [historialGas, setHistorialGas] = useState<Record<string, { fecha: string, valor: string, km: number, rendimiento: number }[]>>(() => {
    try {
      const saved = Storage.get('motopikis_historial_gas');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    Storage.set('motopikis_metricas', JSON.stringify(metricasMotos));
  }, [metricasMotos]);
  
  useEffect(() => {
    Storage.set('motopikis_historial_gas', JSON.stringify(historialGas));
  }, [historialGas]);

  const exportarHistorialPDF = async () => {
    try {
      const doc = new jsPDF() as any;
      doc.setFillColor(31, 41, 55);
      doc.rect(0, 0, 210, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      
      const motoObj = motosReales.find((m: any) => m.id.toString() === motoActiva);
      const motoName = motoObj ? (motoObj.apodo || motoObj.placa) : "Vehículo";

      doc.text(`Historial de Tanqueos - ${motoName.toUpperCase()}`, 15, 16);
      
      const dataInfo = historialGas[motoActiva] || [];
      let totalCosto = 0;
      let totalGalones = 0;
      
      dataInfo.forEach((row: any, i: number) => {
         if (row.valor) {
           const vStr = String(row.valor).replace(/[^0-9]/g, '');
           const v = parseInt(vStr, 10);
           if (!isNaN(v)) totalCosto += v;
         }
         if (row.galones) {
           totalGalones += row.galones;
         } else if (row.rendimiento > 0 && i < dataInfo.length - 1) {
           const prev = dataInfo[i+1];
           const diffKm = row.km - prev.km;
           totalGalones += (diffKm / row.rendimiento);
         }
      });
      
      const totalKmAndados = dataInfo.length > 1 ? Math.max(0, dataInfo[0].km - dataInfo[dataInfo.length - 1].km) : 0;
      const consumoPromedioTotal = totalGalones > 0 ? Math.round(totalKmAndados / totalGalones) : 0;
      const fechasPeriodo = dataInfo.length > 0 ? `${dataInfo[dataInfo.length - 1].fecha} a ${dataInfo[0].fecha}` : 'Sin registros';

      autoTable(doc, {
        startY: 35,
        head: [['Fecha', 'Costo', 'Odómetro', 'Consumo Prom.']],
        body: dataInfo.map((row: any) => [
          row.fecha, 
          `${row.valor}`, 
          `${new Intl.NumberFormat('es-CO').format(row.km)} km`, 
          `${row.rendimiento} Km/Gl`
        ]),
        headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 5 },
      });
      
      const finalY = (doc as any).lastAutoTable.finalY + 15;
      doc.setFillColor(34, 197, 94);
      doc.rect(14, finalY, 182, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text("RESUMEN GLOBAL DE CONSUMO", 15, finalY + 6);
      
      doc.setTextColor(50, 50, 50);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Periodo Analizado:`, 15, finalY + 16); doc.text(fechasPeriodo, 90, finalY + 16);
      doc.text(`Kms Recorridos:`, 15, finalY + 23); doc.text(`${new Intl.NumberFormat('es-CO').format(totalKmAndados)} km`, 90, finalY + 23);
      doc.text(`Galones Surtidos:`, 15, finalY + 30); doc.text(`${totalGalones.toFixed(2)} gal`, 90, finalY + 30);
      doc.text(`Gasto Monetario Acumulado:`, 15, finalY + 37); doc.text(`${new Intl.NumberFormat('es-CO').format(totalCosto)} COP`, 90, finalY + 37);
      
      doc.setFont("helvetica", "bold");
      doc.text(`Rendimiento Promedio Vital:`, 15, finalY + 47); doc.text(`${consumoPromedioTotal} Km/Galón`, 90, finalY + 47);

      const fileName = `Historial-${motoName.replace(/\s+/g, '-')}.pdf`;
      const pdfBlob = doc.output("blob");
      
      if (navigator.share && navigator.canShare) {
         try {
           const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
           if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: 'Historial de Tanqueos',
                text: 'Mi historial de consumo de combustible exportado desde MOTOPIKIS Pro.'
              });
              return; // Exito
           }
         } catch (shareErr: any) {
           if (shareErr.name === 'AbortError') return; // User cancelled
         }
      }

      // Fallback para PC / Navegadores sin Web Share API
      doc.save(fileName);
    } catch(err) {
      mostrarAviso("Error exportando el PDF: " + String(err), 'error');
    }
  };

  // Estados para Registro de Nueva Moto (Formulario Real Supabase)
  const [mostrarModalNuevaMoto, setMostrarModalNuevaMoto] = useState(false);
  const [nuevaMotoPlaca, setNuevaMotoPlaca] = useState('');
  const [nuevaMotoMarca, setNuevaMotoMarca] = useState('');
  const [nuevaMotoModelo, setNuevaMotoModelo] = useState('');
  const [nuevaMotoCC, setNuevaMotoCC] = useState('');
  const [nuevoSoat, setNuevoSoat] = useState('');
  const [nuevaTecno, setNuevaTecno] = useState('');
  const [nuevoImpuesto, setNuevoImpuesto] = useState('');
  const [nuevaPoliza, setNuevaPoliza] = useState('');
  const [guardandoMoto, setGuardandoMoto] = useState(false);

  const formatMiles = (val: string) => {
    const n = val.replace(/\D/g, '');
    return n ? new Intl.NumberFormat('es-CO').format(parseInt(n, 10)) : '';
  };

  const formatPlacaMoto = (val: string) => {
    const cleaned = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (config.code === 'CO') {
      if (cleaned.length <= 3) return cleaned;
      return cleaned.slice(0, 3) + '-' + cleaned.slice(3, 6);
    }
    return cleaned;
  };

// formatTitleCase movido temporalmente...
  const formatTitleCaseTemp = (str: string) => {
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
  };

  const docActual = React.useMemo<any>(() => {
    const motoData = motosReales.find(m => m.id.toString() === motoActiva);
    const docs = motoData?.documentos_legales || [];
    
    // Sorting by created_at or id to get the latest
    // Supabase will bring an array, we get the ones matching type
    const getDoc = (tipo: string) => {
       const fd = docs.filter((d: any) => d.tipo_documento === tipo);
       return fd.length > 0 ? fd[fd.length - 1].fecha_vencimiento : null;
    };
    
    const evalExpired = (fechaStr: string | null) => {
        if (!fechaStr) return { isSet: false, isExpired: true }; // 🛡️ COMPORTAMIENTO VIP: Sin datos = Vencido
        const hoyC = new Date();
        const [y, m, d] = fechaStr.split('-');
        if (!y || !m || !d) return { isSet: true, isExpired: false };
        const dDate = new Date(Number(y), Number(m)-1, Number(d));
        hoyC.setHours(0,0,0,0);
        return { isSet: true, isExpired: dDate < hoyC };
    };

    const soatVal = getDoc('SOAT');
    const tecnoVal = getDoc('TECNOMECANICA');
    const impuestoVal = getDoc('IMPUESTO');
    
    const sSoat = evalExpired(soatVal);
    const sTecno = evalExpired(tecnoVal);
    const sImpuesto = evalExpired(impuestoVal);
    
    // Simit Logic
    const fdSimit = docs.filter((d: any) => d.tipo_documento === 'SIMIT');
    const simitObj = fdSimit.length > 0 ? fdSimit[fdSimit.length - 1] : null;

    const baseLegales = [
        { id: 'simit', title: 'Multas / Tránsito', text: simitObj ? (simitObj.estado === 'OK' ? 'Paz y Salvo ✔️' : 'Tiene Multas🚨') : 'Sincronizar Tránsito', status: simitObj ? (simitObj.estado === 'OK' ? 'ACTUALIZAR' : 'RENOVAR🚨') : 'CONSULTAR', color: simitObj ? (simitObj.estado === 'OK' ? 'text-green-500' : 'text-danger-red') : 'text-primary-cyan', border: simitObj ? (simitObj.estado === 'OK' ? 'border-l-green-500' : 'border-l-danger-red') : 'border-l-primary-cyan', btn: true },
        { id: 'soat', title: config.code === 'CO' ? 'SOAT' : 'Seguro Obligatorio', text: sSoat.isSet ? (sSoat.isExpired ? `Vencido: ${soatVal}` : `Vence: ${soatVal}`) : '⚠️ Sin Datos (Crítico)', status: sSoat.isExpired ? 'RENOVAR🚨' : 'ACTUALIZAR', color: sSoat.isExpired ? 'text-danger-red' : 'text-green-500', border: sSoat.isExpired ? 'border-l-danger-red' : 'border-l-green-500', btn: true },
        { id: 'tecno', title: 'Tecno Mecánica', text: sTecno.isSet ? (sTecno.isExpired ? `Vencida: ${tecnoVal}` : `Vence: ${tecnoVal}`) : '⚠️ Sin Datos (Crítico)', status: sTecno.isExpired ? 'RENOVAR🚨' : 'ACTUALIZAR', color: sTecno.isExpired ? 'text-danger-red' : 'text-green-500', border: sTecno.isExpired ? 'border-l-danger-red' : 'border-l-green-500', btn: true },
        { id: 'impuesto', title: 'Impuesto Vehicular', text: sImpuesto.isSet ? (sImpuesto.isExpired ? `Vencido: ${impuestoVal}` : `Vence: ${impuestoVal}`) : '⚠️ Sin Datos (Crítico)', status: sImpuesto.isExpired ? 'PAGAR🚨' : 'ACTUALIZAR', color: sImpuesto.isExpired ? 'text-danger-red' : 'text-green-500', border: sImpuesto.isExpired ? 'border-l-danger-red' : 'border-l-green-500', btn: true }
    ];
    
    const expected = ['SOAT', 'TECNOMECANICA', 'IMPUESTO', 'SIMIT'];
    const otherDocs = docs.filter((d: any) => !expected.includes(d.tipo_documento) && !d.tipo_documento?.startsWith('GARANTIA_||_'));
    const processedOtherTypes = new Set();
    otherDocs.sort((a: any, b: any) => (a.id > b.id ? -1 : 1)).forEach((d: any) => {
       if(!processedOtherTypes.has(d.tipo_documento)) {
         processedOtherTypes.add(d.tipo_documento);
         const sOther = evalExpired(d.fecha_vencimiento);
         baseLegales.push({
           id: d.tipo_documento,
           db_id: d.id,
           title: d.tipo_documento,
           text: sOther.isSet ? (sOther.isExpired ? `Vencido: ${d.fecha_vencimiento}` : `Vence: ${d.fecha_vencimiento}`) : 'Sin Información',
           status: sOther.isSet ? (sOther.isExpired ? 'RENOVAR🚨' : 'ACTUALIZAR') : 'CARGAR',
           color: sOther.isSet ? (sOther.isExpired ? 'text-danger-red' : 'text-green-500') : 'text-slate-300',
           border: sOther.isSet ? (sOther.isExpired ? 'border-l-danger-red' : 'border-l-green-500') : 'border-l-slate-600',
           btn: true
         });
       }
    });

    const cons = motoData?.consumibles_moto || [];
    const baseConsumibles = cons.map((c: any) => {
       const kmActual = metricasMotos[motoActiva]?.km || 0;
       const kmRecorridos = kmActual - c.km_instalacion;
       const kmRestantes = c.km_duracion - kmRecorridos;
       const percent = Math.max(0, Math.min(100, Math.floor((kmRestantes / c.km_duracion) * 100)));
       let color = 'green-500';
       let label = 'ÓPTIMO';
       if (percent <= 20) { color = 'danger-red'; label = 'VENCIDO'; }
       else if (percent <= 50) { color = 'amber-500'; label = 'MITAD'; }
       return {
          id: c.id,
          name: c.nombre_repuesto,
          percent,
          color,
          label,
          subtext: kmRestantes <= 0 ? 'Desgaste Crítico, cámbialo.' : `Restan ${new Intl.NumberFormat('es-CO').format(kmRestantes)} km`,
          km_restantes: kmRestantes,
          valor: c.valor_repuesto || 0,
          instalacion: c.costo_instalacion || 0,
          km_duracion: c.km_duracion
       };
    });

    const garantiasDocs = docs.filter((d: any) => d.tipo_documento && d.tipo_documento.startsWith('GARANTIA_||_')).sort((a:any, b:any) => a.fecha_vencimiento.localeCompare(b.fecha_vencimiento));
    const baseGarantia = garantiasDocs.map((g: any) => {
       const [y, m, d] = g.fecha_vencimiento.split('-');
       const programmed = new Date(Number(y), Number(m)-1, Number(d));
       const today = new Date();
       today.setHours(0,0,0,0);
       const isPast = programmed < today;

       const parts = g.tipo_documento.split('_||_');
       const titleParsed = parts[1] || 'Revisión Programada';
       const valorParsed = parts[2] || '0';
       const valorFmt = Number(valorParsed) > 0 ? `$${new Intl.NumberFormat('es-CO').format(Number(valorParsed))}` : '';

       return {
          id: g.id,
          title: titleParsed,
          text: `Fecha: ${g.fecha_vencimiento}`,
          valorInfo: valorFmt,
          done: g.estado === 'HECHO',
          past: isPast
       };
    });

    return {
      legales: baseLegales,
      consumibles: baseConsumibles,
      garantia: baseGarantia
    };
  }, [motosReales, motoActiva, metricasMotos]);

  const isActivaExpirada = () => {
    const moto = motosReales.find((m: any) => m.id.toString() === motoActiva);
    if (!moto || !moto.documentos_legales) return false;
    const hoy = new Date();
    hoy.setHours(0,0,0,0);
    const latestDocs = new Map();
    moto.documentos_legales.forEach((d: any) => { latestDocs.set(d.tipo_documento, d); });
    for (const doc of latestDocs.values()) {
       if (doc.tipo_documento === 'SIMIT') {
           if (doc.estado !== 'OK') return true;
       } else if (doc.fecha_vencimiento && !doc.tipo_documento?.startsWith('GARANTIA_||_')) {
          const [y, m, d] = doc.fecha_vencimiento.split('-');
          if (y && m && d) {
             const vDate = new Date(Number(y), Number(m)-1, Number(d));
             if (vDate < hoy) return true;
          }
       }
    }
    return false;
  };

  const generarCertificadoOro = () => {
    if (isOro) {
       const motoData = motosReales.find(m => m.id.toString() === motoActiva);
       
       if (!motoData) {
         mostrarAviso('⚠️ GARAGE VACÍO: Debes registrar un vehículo en el Garaje antes de poder expedir un Certificado ORO.', 'error');
         return;
       }

       if (isActivaExpirada()) {
          mostrarAviso('No puedes expedir el certificado ORO porque tienes documentos vehiculares vencidos. Ponlos al día primero.', 'error');
          return;
       }
       
       const placaData = motoData.placa.toUpperCase();
       const randomSalt = Math.floor(Math.random() * 999999).toString(16).toUpperCase();
       const securityHash = `MP-ORO-${placaData.substring(0,3)}-${randomSalt}`;
       
       setHash(securityHash);
       setQrUrl(`https://pikishub.com/motopikispro/?validar=true&placa=${placaData}&serial=${securityHash}`);
       setMostrarQR(true);
    } else {
       setMostrarModalPremium(true);
    }
  };

  const handleGuardarNuevaMoto = async () => {
    // 🛡️ REGLA DE ORO PIKIS: Los 4 pilares de identidad son obligatorios
    if (!nuevaMotoPlaca || !nuevaMotoMarca || !nuevaMotoModelo || !nuevaMotoCC) {
       mostrarAviso("Placa, Marca, Modelo y Cilindraje son obligatorios para el registro.", 'error');
       return;
    }

    // 🔬 VALIDACIÓN DE FORMATO DE PLACA INTERNACIONAL
    const limpia = nuevaMotoPlaca.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!config.plateRegEx.test(limpia)) {
       mostrarAviso(config.plateFormatHelp, 'error');
       return;
    }

    setGuardandoMoto(true);
    
    // Función para capitalizar la primera letra de cada palabra
    const toTitleCase = (str: string) => str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
    
    const marcaFormatted = toTitleCase(nuevaMotoMarca);
    const modeloFormatted = toTitleCase(nuevaMotoModelo);

    try {
      // 1. Obtener ID del usuario activo
      let userId = Storage.get('motopikis_user_id');
      if (!userId) {
        const userStr = Storage.get('motopikis_user');
        if (userStr && userStr.startsWith('{')) {
          try { userId = JSON.parse(userStr).id; Storage.set('motopikis_user_id', userId as string); } catch(e) {}
        }
      }
      if (!userId) {
         const { data: { session } } = await supabase.auth.getSession();
         if (session?.user?.id) {
           userId = session.user.id;
           Storage.set('motopikis_user_id', userId);
         }
      }
      
      if (!userId) throw new Error("No hay un motero logueado conectado.");

      // 2. Insertar Moto en Supabase
      const { data: vehiculoNuevo, error: errVehiculo } = await supabase
        .from('vehiculos')
        .insert([{
          usuario_id: userId,
          placa: nuevaMotoPlaca.toUpperCase(),
          apodo: `${marcaFormatted} ${modeloFormatted}`.trim(),
          cilindraje: parseInt(nuevaMotoCC, 10) || null
        }])
        .select()
        .single();
        
      if (errVehiculo) throw errVehiculo;

      // 3. Insertar SOAT si lo puso
      if (nuevoSoat) {
        await supabase.from('documentos_legales').insert([{
          vehiculo_id: vehiculoNuevo.id,
          tipo_documento: 'SOAT',
          fecha_vencimiento: nuevoSoat,
          estado: 'ACTIVO'
        }]);
      }

      // 4. Insertar Tecno si la puso
      if (nuevaTecno) {
        await supabase.from('documentos_legales').insert([{
          vehiculo_id: vehiculoNuevo.id,
          tipo_documento: 'TECNOMECANICA',
          fecha_vencimiento: nuevaTecno,
          estado: 'ACTIVO'
        }]);
      }
      if (nuevoImpuesto) {
        await supabase.from('documentos_legales').insert([{
          vehiculo_id: vehiculoNuevo.id,
          tipo_documento: 'IMPUESTO',
          fecha_vencimiento: nuevoImpuesto,
          estado: 'ACTIVO'
        }]);
      }
      if (nuevaPoliza) {
        await supabase.from('documentos_legales').insert([{
          vehiculo_id: vehiculoNuevo.id,
          tipo_documento: 'POLIZA',
          fecha_vencimiento: nuevaPoliza,
          estado: 'ACTIVO'
        }]);
      }

      mostrarAviso("¡Vehículo y Documentos asegurados en la Bóveda Supabase!", 'success');
      const newId = vehiculoNuevo.id.toString();
      setMotoActiva(newId);
      setMostrarModalNuevaMoto(false);
      
      // Marcamos el ID específico para saltar hacia él en cuanto aparezca en el DOM
      setIdSaltoPendiente(newId);
      setTriggerSync(s => s + 1);

      // Limpiar campos
      setNuevaMotoPlaca(''); setNuevaMotoMarca(''); setNuevaMotoModelo(''); setNuevaMotoCC(''); setNuevoSoat(''); setNuevaTecno(''); setNuevoImpuesto(''); setNuevaPoliza('');

    } catch (err: any) {
      console.error(err);
      mostrarAviso("Error al guardar en Supabase: " + err.message, 'error');
    } finally {
      setGuardandoMoto(false);
    }
  };
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col min-h-full relative pb-4"
    >
      <div className="flex justify-between items-center mb-4 gap-2 pr-1">
        <h2 className="text-xl font-extrabold truncate shrink min-w-0 flex items-center gap-2.5">
          <span>Garaje {userNameAlias || 'Usuario'}</span>
        </h2>
      </div>
      
      <div className="relative w-full mb-5">
        {!cargandoMotos && activeCardIndex > 0 && (
          <button 
             onClick={() => document.getElementById('motoScrollContainer')?.scrollBy({ left: -250, behavior: 'smooth'})}
             className="absolute left-1 top-1/2 -translate-y-1/2 z-30 p-1.5 bg-slate-950/80 backdrop-blur-xl rounded-full border border-white/20 shadow-[0_0_20px_rgba(0,0,0,0.9)] text-white hover:bg-slate-800 transition-all opacity-80 hover:opacity-100 active:scale-95"
          >
             <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        
        {!cargandoMotos && activeCardIndex < motosReales.length && (
          <button 
             onClick={() => document.getElementById('motoScrollContainer')?.scrollBy({ left: 250, behavior: 'smooth'})}
             className="absolute right-1 top-1/2 -translate-y-1/2 z-30 p-1.5 bg-slate-950/80 backdrop-blur-xl rounded-full border border-white/20 shadow-[0_0_20px_rgba(0,0,0,0.9)] text-white hover:bg-slate-800 transition-all opacity-80 hover:opacity-100 active:scale-95"
          >
             <ChevronRight className="w-4 h-4" />
          </button>
        )}
      <div 
        id="motoScrollContainer"
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-2 shrink-0 w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        onScroll={(e) => {
          const node = e.currentTarget;
          const cw = node.offsetWidth * 0.95;
          if (cw > 0) {
             const idx = Math.round(node.scrollLeft / cw);
             if (idx !== activeCardIndex) {
                setActiveCardIndex(idx);
                if (motosReales && idx < motosReales.length) {
                   setMotoActiva(motosReales[idx].id.toString());
                } else {
                   // Si el índice es mayor o igual al numero de motos, asumimos que está en la tarjeta de registro/premium
                   setMotoActiva('NUEVA_ACCION');
                }
             }
          }
        }}
      >
        {cargandoMotos ? (
          <div className="bg-slate-800/50 animate-pulse rounded-3xl py-2.5 px-5 flex items-center justify-center text-center w-[95%] max-w-[380px] shrink-0 min-h-[110px]">
             <span className="text-white/50 text-xs font-bold tracking-widest">SINCRONIZANDO GARAJE...</span>
          </div>
        ) : (
          <>
            {motosReales.map((moto: any) => {
          const mId = moto.id.toString();
          const isActive = motoActiva === mId;
          
          let computedStatus = 'base' as any;
          if (moto.id === 'MOCK_CERT') computedStatus = 'certificado';
          
          // Lógica estricta de jerarquía en vivo evaluando SÓLO la última versión de cada doc
          if (moto.documentos_legales) {
              const hoy = new Date();
              hoy.setHours(0,0,0,0);
              
              const latestDocs = new Map();
              moto.documentos_legales.forEach((d: any) => {
                 latestDocs.set(d.tipo_documento, d);
              });
              
              for (const doc of latestDocs.values()) {
                 if (doc.tipo_documento === 'SIMIT') {
                     if (doc.estado !== 'OK') {
                         computedStatus = 'expirado';
                         break;
                     }
                 } else if (doc.fecha_vencimiento && !doc.tipo_documento?.startsWith('GARANTIA_||_')) {
                    const [y, m, d] = doc.fecha_vencimiento.split('-');
                    if (y && m && d) {
                       const vDate = new Date(Number(y), Number(m)-1, Number(d));
                       if (vDate < hoy) {
                          computedStatus = 'expirado';
                          break;
                       }
                    }
                 }
              }
          }

          return (
            <div 
              key={mId} 
              id={`moto-card-${mId}`}
              onClick={() => setMotoActiva(mId)} 
              className={`backdrop-blur-md rounded-3xl p-3 text-center w-[95%] max-w-[380px] shrink-0 snap-start relative flex flex-col justify-center min-h-[140px] cursor-pointer transition-all ${isActive ? 'bg-gradient-to-br from-yellow-500/15 via-slate-900/60 to-slate-900/80 border border-yellow-500/40 shadow-[0_0_20px_rgba(251,191,36,0.15)] backdrop-blur-2xl' : 'bg-slate-800/80 border border-white/10 opacity-60 hover:opacity-100 shadow-[0_8px_30px_rgba(0,0,0,0.4)]'}`}>
              
              {isActive && (
                <div 
                  className={`absolute top-0 right-0 px-4 py-1.5 rounded-bl-3xl rounded-tr-[18px] text-[9.5px] font-black tracking-widest backdrop-blur-md border-b border-l z-20 ${
                    computedStatus === 'expirado' ? 'bg-danger-red/15 border-danger-red/40 text-danger-red drop-shadow-[0_0_10px_rgba(239,68,68,0.8)]' :
                    isOro ? 'bg-gold-cert/15 border-gold-cert/40 text-gold-cert drop-shadow-[0_0_10px_rgba(251,191,36,0.8)]' :
                    'bg-primary-cyan/15 border-primary-cyan/40 text-primary-cyan drop-shadow-[0_0_10px_rgba(0,168,255,0.8)]'
                  }`}
                >
                  {computedStatus === 'expirado' ? 'DOCS. VENCIDOS' :
                   isOro ? 'DOCS. AL DÍA' : 
                   'DOCS. AL DÍA'}
                </div>
              )}

              {/* GRID 2 COLUMNAS (Izquierda Identidad, Derecha Métricas) */}
              <div className="flex w-full h-full gap-3 mt-5 relative z-10">
                 
                 {/* COLUMNA IZQUIERDA: Identidad */}
                 <div className="flex flex-col flex-1 items-center justify-center border-r border-white/10 pr-3 relative">
                    
                    <div className="absolute -top-5 w-full flex justify-center">
                      {isOro ? (
                        <div className="text-gold-cert flex justify-center items-center gap-1.5 text-[12px] font-black uppercase drop-shadow-[0_2px_4px_rgba(251,191,36,0.2)]">
                          👑 PREMIUM ORO
                        </div>
                      ) : (
                        <div className="text-white/90 hover:text-white flex justify-center items-center gap-1.5 text-[12px] font-black uppercase cursor-pointer transition-colors drop-shadow-md"
                             onClick={(e) => { e.stopPropagation(); setMostrarModalPremium(true); if (onOpenMembresia) onOpenMembresia(); }}>
                          👑 HAZTE ORO
                        </div>
                      )}
                    </div>
                    
                    <h3 className="font-bold text-[15px] sm:text-[17px] text-white leading-tight w-full text-center mb-3 mt-6">
                       {moto.apodo || `${moto.marca} ${moto.modelo}`}
                    </h3>

                    <div className="bg-yellow-500 text-black font-black tracking-widest py-1 px-4 text-center rounded uppercase border-2 border-yellow-600 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] text-[18px] sm:text-[20px] inline-block whitespace-nowrap min-w-[100px] font-sans">
                       {moto.placa}
                    </div>

                    <button 
                      onClick={(e) => { e.stopPropagation(); handleEliminarMoto(mId, moto.placa); }}
                      className="mt-1.5 p-1.5 rounded-full text-yellow-500 bg-yellow-500/10 hover:bg-yellow-500 hover:text-[#070b14] transition-colors z-20 relative flex items-center justify-center"
                      title="Eliminar Moto"
                    >
                      <Trash2 className="w-[12px] h-[12px]" />
                    </button>
                 </div>

                 {/* COLUMNA DERECHA: Consumo y Odómetro */}
                 <div className="flex flex-col flex-1 gap-2 justify-center pl-1 pt-3">
                    
                    {/* Botón Consumo */}
                    <button 
                       onClick={(e) => { 
                          e.stopPropagation(); 
                          if (isActive) {
                            const userKm = metricasMotos[mId]?.km || 0;
                            if (userKm > 0) {
                              setMostrarModalGas(true);
                            } else {
                              setDialogoComp({
                                titulo: 'ACCIÓN REQUERIDA',
                                msg: 'Primero Ingresa datos en el Odómetro para activar el registro de consumo, después de tu segundo registro calcularemos tu rendimiento de combustible',
                                accion: () => setDialogoComp(null)
                              });
                            }
                          } 
                        }}
                       className={`w-[90%] block mx-auto -translate-x-[6px] h-[46px] ${isActive ? ( (metricasMotos[mId]?.km || 0) > 0 ? 'bg-green-500/15 border-green-500/30 text-green-400 backdrop-blur-md' : 'bg-white/10 border-white/10 text-slate-400 opacity-70' ) : 'bg-slate-800/50 border-white/10 text-slate-500'} hover:opacity-80 border rounded-lg px-2 flex flex-col items-center justify-center transition-all active:scale-95 shadow-sm`}
                    >
                       <span className="text-[10px] uppercase tracking-widest font-black flex items-center justify-center gap-1"><Droplet className="w-3 h-3" /> CONSUMO</span>
                       <span className="text-[14px] font-black font-mono">
                         {isActive && metricasMotos[mId]?.rendimiento > 0 ? `${metricasMotos[mId].rendimiento} Km/Gl` : (isActive ? 'Calculando' : '--')}
                       </span>
                    </button>

                    {/* Botón Odómetro */}
                    <button 
                       onClick={(e) => { e.stopPropagation(); if (isActive) setMostrarModalKm(true); }}
                       className={`w-[90%] block mx-auto -translate-x-[6px] h-[46px] ${isActive ? 'bg-blue-500/15 border-blue-500/30 text-[#00a8ff] backdrop-blur-md' : 'bg-slate-800/50 border-white/10 text-slate-500'} hover:opacity-80 border rounded-lg px-2 flex flex-col items-center justify-center transition-all active:scale-95 shadow-sm`}
                    >
                       <span className="text-[10px] uppercase tracking-widest font-black flex items-center justify-center gap-1"><Gauge className="w-3 h-3" /> ODÓMETRO</span>
                       <span className="text-[14px] font-black font-mono">
                         {isActive ? `${new Intl.NumberFormat('es-CO').format(metricasMotos[mId]?.km || 0)} Kms` : '--'}
                       </span>
                    </button>

                 </div>
              </div>
            </div>
          );
        })}

        {/* Vehículo (Añadir Moto - Límite 1 Moto Gratis, Ilimitado ORO) */}
        {(motosReales.length === 0 || isOro) ? (
          <motion.div 
            layout
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setMotoActiva('NUEVA_ACCION');
              setMostrarModalNuevaMoto(true);
            }} 
            className={`min-h-[140px] ${esModoVinculacion ? 'border-primary-cyan shadow-[0_0_30px_rgba(0,168,255,0.2)] bg-primary-cyan/10' : 'border-primary-cyan/20 bg-slate-900/40 shadow-none'} border-2 border-dashed rounded-[32px] py-4 px-6 text-center w-[95%] max-w-[380px] shrink-0 snap-start hover:border-primary-cyan/50 hover:bg-primary-cyan/10 cursor-pointer flex flex-col justify-center items-center group relative overflow-hidden transition-all duration-500`}
          >
            <div className="relative mb-3 flex flex-col items-center">
              <div className={`absolute inset-0 bg-primary-cyan/20 blur-xl rounded-full ${esModoVinculacion ? 'opacity-100 scale-150 animate-pulse' : 'opacity-0 scale-100'} transition-all duration-700`}></div>
              
              {/* Ondas de energía (Energy Ripples) */}
              {esModoVinculacion && (
                <>
                  <motion.div 
                    animate={{ scale: [1, 2.2], opacity: [0.6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                    className="absolute w-16 h-16 border-2 border-primary-cyan/50 rounded-full"
                  />
                  <motion.div 
                    animate={{ scale: [1, 2.8], opacity: [0.3, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.7 }}
                    className="absolute w-16 h-16 border border-primary-cyan/30 rounded-full"
                  />
                </>
              )}

              <div 
                className={`relative ${esModoVinculacion ? 'w-16 h-16' : 'w-12 h-12'} bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(0,168,255,0.4)] transition-all duration-500 animate-bounce-slow`}
              >
                <motion.div
                  animate={esModoVinculacion ? {
                    opacity: [1, 0.4, 1, 0.6, 1],
                    scale: [1, 1.15, 1],
                    filter: [
                      "brightness(1) drop-shadow(0 0 5px #fff)", 
                      "brightness(2.2) drop-shadow(0 0 20px #22d3ee)", 
                      "brightness(1) drop-shadow(0 0 5px #fff)"
                    ]
                  } : {}}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <PlusCircle className={`${esModoVinculacion ? 'w-9 h-9' : 'w-7 h-7'} text-bg-dark transition-all duration-500`} />
                </motion.div>
              </div>
            </div>
            <h3 className={`font-black ${esModoVinculacion ? 'text-[15px]' : 'text-[13px]'} text-white uppercase tracking-tighter transition-all duration-500`}>Vincula Moto y/o Carro</h3>
            <p className={`text-[10px] text-slate-400 font-bold mt-1.5 opacity-80 transition-all duration-500`}>Haz crecer tu flota en la Bóveda Pikis</p>
          </motion.div>
        ) : (
          <div onClick={() => {
            if (onOpenMembresia) onOpenMembresia();
            else setMostrarModalPremium(true);
          }} className="bg-transparent border-2 border-dashed border-gold-cert/30 rounded-3xl py-3 px-6 text-center w-[95%] max-w-[380px] shrink-0 snap-start opacity-80 hover:opacity-100 hover:bg-gold-cert/5 transition-all cursor-pointer flex flex-col justify-center items-center min-h-[130px]">
            <Crown className="w-8 h-8 text-gold-cert mb-2" />
            <h3 className="font-bold text-sm text-gold-cert">Más Espacio Garaje</h3>
            <p className="text-[10px] text-gold-cert/70 mt-1">Requiere Membresía ORO</p>
          </div>
        )}
          </>
        )}
      </div>
      </div>
      
      <div className="relative">
        <AnimatePresence>
          {esModoVinculacion && (
             <motion.div 
               initial={{ opacity: 0, scale: 0.9, y: 10 }}
               animate={{ opacity: 1, scale: 1, y: 0 }}
               exit={{ opacity: 0, scale: 0.9, y: 10 }}
               className="absolute inset-0 flex flex-col items-center justify-center p-4 z-10 pointer-events-none"
             >
                {/* Resplandor pulsante de fondo */}
                <motion.div 
                  animate={{ 
                    opacity: [0.1, 0.3, 0.1], 
                    scale: [1, 1.2, 1],
                  }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute w-72 h-72 bg-primary-cyan/25 blur-[60px] rounded-full"
                />
                
                {/* Roadmap de Registro */}
                <div className="flex justify-between w-full max-w-[340px] relative z-20 px-2">
                   <div className="flex flex-col items-center gap-2 group/step">
                      <div className="w-11 h-11 rounded-2xl bg-slate-900/80 border border-white/10 flex items-center justify-center">
                         <MapPin className="w-5 h-5 text-slate-500" />
                      </div>
                      <span className="text-[9px] font-black text-white/40 uppercase tracking-tighter text-center whitespace-nowrap">VINCULA TU PLACA</span>
                   </div>
                   
                   <div className="flex-1 h-[1px] bg-white/5 mt-5.5 mx-2 self-start" />
                   
                   <div className="flex flex-col items-center gap-2 group/step">
                      <div className="w-11 h-11 rounded-2xl bg-slate-900/80 border border-white/10 flex items-center justify-center">
                         <Shield className="w-5 h-5 text-slate-500" />
                      </div>
                      <span className="text-[9px] font-black text-white/40 uppercase tracking-tighter text-center whitespace-nowrap">PROTEGE TUS DOCS</span>
                   </div>
                   
                   <div className="flex-1 h-[1px] bg-white/5 mt-5.5 mx-2 self-start" />
                   
                   <div className="flex flex-col items-center gap-2 group/step">
                      <div className="w-11 h-11 rounded-2xl bg-slate-900/80 border border-white/10 flex items-center justify-center">
                         <Zap className="w-5 h-5 text-slate-500" />
                      </div>
                      <span className="text-[9px] font-black text-white/40 uppercase tracking-tighter text-center whitespace-nowrap">CONTROL TOTAL</span>
                   </div>
                </div>
                
                <p className="text-[8px] text-primary-cyan/50 font-black uppercase tracking-[0.2em] mt-8 animate-pulse">Bóveda MOTOPIKIS Pro activada</p>
             </motion.div>
          )}
        </AnimatePresence>

        <motion.div 
          animate={{ 
            opacity: esModoVinculacion ? 0 : 1,
            scale: esModoVinculacion ? 0.95 : 1,
            filter: esModoVinculacion ? 'blur(10px)' : 'blur(0px)',
            pointerEvents: esModoVinculacion ? 'none' : 'auto'
          }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          {(() => {
              const dActual = docActual || { legales: [], consumibles: [], garantia: [] };
              const tieneAlertasL = dActual.legales.some((l: any) => l.status.includes('RENOVAR') || l.status.includes('PAGAR') || l.status.includes('Expirado') || l.status.includes('VENCIDO') || l.status.includes('CRÍTICO'));
              const tieneAlertasC = dActual.consumibles.some((c: any) => c.percent <= 20);
              const tieneAlertasG = dActual.garantia.some((g: any) => g.past && !g.done);
              const tieneAlertasTotales = tieneAlertasL || tieneAlertasC || tieneAlertasG;

             return (
                <div className="grid grid-cols-2 gap-3 mb-2 px-1">
                   <div onClick={() => setPestañaActiva('legales')} 
                     className="border rounded-2xl p-3 flex flex-col justify-center items-start cursor-pointer transition-all active:scale-95 shadow-[0_4px_15px_rgba(0,0,0,0.1)] min-h-[85px] backdrop-blur-md bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20">
                      <h3 className="font-black text-[13px] tracking-wide mb-0.5 text-yellow-400">LEGALES</h3>
                      <p className="text-[10px] text-white font-bold leading-tight uppercase">BÓVEDA LEGAL</p>
                      <p className="text-[9px] text-white leading-tight mt-0.5">(Tus pólizas y documentos).</p>
                   </div>
                   
                   <div onClick={() => setPestañaActiva('consumibles')} 
                     className="border rounded-2xl p-3 flex flex-col justify-center items-start cursor-pointer transition-all active:scale-95 shadow-[0_4px_15px_rgba(0,0,0,0.1)] min-h-[85px] backdrop-blur-md bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20">
                       <h3 className="font-black text-[13px] tracking-wide mb-0.5 text-yellow-400">CONSUMIBLES</h3>
                       <p className="text-[10px] text-white font-bold leading-tight uppercase">DESGASTE DE PIEZAS</p>
                       <p className="text-[9px] text-white leading-tight mt-0.5">(Vida útil de componentes).</p>
                   </div>
                   
                   <div onClick={() => setPestañaActiva('garantia')} 
                     className="border rounded-2xl p-3 flex flex-col justify-center items-start cursor-pointer transition-all active:scale-95 shadow-[0_4px_15px_rgba(0,0,0,0.1)] min-h-[85px] backdrop-blur-md bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20">
                       <h3 className="font-black text-[13px] tracking-wide mb-0.5 text-yellow-400">GARANTÍAS</h3>
                       <p className="text-[10px] text-white font-bold leading-tight uppercase">REVISIÓN CÍCLICAS</p>
                       <p className="text-[9px] text-white leading-tight mt-0.5">(Historial y garantías).</p>
                   </div>
                   
                   <div onClick={() => setPestañaActiva('alertas')} 
                     className={`border rounded-2xl p-3 flex flex-col justify-center items-start cursor-pointer transition-all active:scale-95 shadow-[0_4px_15px_rgba(0,0,0,0.1)] min-h-[85px] backdrop-blur-md ${tieneAlertasTotales ? 'bg-danger-red/25 border-danger-red/60 animate-pulse' : 'bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20'}`}>
                       <h3 className={`font-black text-[13px] tracking-wide mb-0.5 ${tieneAlertasTotales ? 'text-danger-red drop-shadow-[0_0_8px_rgba(239,68,68,0.8)]' : 'text-yellow-400'}`}>ALERTAS</h3>
                       <p className="text-[9px] text-white leading-tight mt-0.5">(Centro de Avisos).</p>
                   </div>
                </div>
             );
          })()}
        </motion.div>
      </div>

       <div className="mt-3 mb-2 px-1 flex justify-center">
          <button 
            onClick={generarCertificadoOro}
            className="w-[75%] py-1.5 px-4 bg-gradient-to-r from-gold-cert to-yellow-600 rounded-xl font-black text-[11px] uppercase tracking-widest text-yellow-950 border border-yellow-300/60 transition-all flex justify-center items-center gap-2 active:scale-95 hover:brightness-110 shadow-none"
          >
            <ShieldCheck className="w-4 h-4 text-yellow-950" />
            EMITIR CERTIFICADO ORO
          </button>
       </div>

      {/* BOTÓN INFERIOR DE MONETIZACIÓN - FASE 4 (Exclusivo de Colombia) */}
      {config.code === 'CO' && (
        <div className="mt-1 mb-2 px-1 flex justify-center">
           <button 
             onClick={() => setPestañaActiva('certificados')}
             className="w-[75%] py-0.5 px-4 bg-gradient-to-r from-blue-700 to-cyan-500 border-none rounded-xl font-black text-[10px] uppercase tracking-widest text-white shadow-[0_0_15px_rgba(6,182,212,0.4)] hover:shadow-[0_0_20px_rgba(6,182,212,0.6)] hover:brightness-110 transition-all flex flex-col justify-center items-center gap-0 active:scale-95 ring-2 ring-cyan-400/50"
           >
             <span className="leading-none mt-1 drop-shadow-sm">ADQUIERE TUS CERTIFICADOS</span>
             <span className="text-[8px] text-cyan-50 capitalize leading-none tracking-wide mb-1 drop-shadow-sm">(Comparendos SIMIT - RUNT)</span>
           </button>
        </div>
      )}

      {/* --- MODALES DEL DASHBOARD --- */}
      <AnimatePresence>
        {pestañaActiva === 'certificados' && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} className="fixed inset-0 z-[100] bg-[#050505] overflow-y-auto p-4 pt-12 pb-8 sm:p-6 flex flex-col items-center justify-start h-screen w-screen">
            <button onClick={() => setPestañaActiva('')} className="fixed top-4 right-4 z-[110] p-2 text-white/50 hover:text-white bg-slate-900 hover:bg-danger-red rounded-full transition-all border border-white/10 shadow-[0_0_20px_rgba(0,0,0,0.8)]">
              <X className="w-5 h-5" />
            </button>
            <div className="w-full max-w-md relative text-center pb-12 mt-4">
              <div className="flex flex-col items-center justify-center mb-6">
                 <ShieldCheck className="w-12 h-12 text-yellow-500 mb-2 drop-shadow-[0_0_15px_rgba(234,179,8,0.5)]" />
                 <h4 className="text-white font-black text-[18px] tracking-wide uppercase leading-tight">Certificados<br/>Oficiales Pikis</h4>
                 <p className="text-[11px] text-slate-400 font-medium tracking-wide mt-2 px-2 leading-snug">Solicita tus documentos 100% legales mediante nuestra <span className="text-yellow-500 font-bold">Conserjería VIP Manual</span>. Sin filas, sin estrés.</p>
              </div>

              <div className="flex flex-col gap-3 px-1">
                 {[
                    { id: 'simit', title: '1. COMPARENDOS SIMIT', desc: 'Validación prioritaria de infracciones nacionales SIMIT.', icon: '🚨' },
                    { id: 'runt', title: '2. CERTIFICADO DE TRÁNSITO', desc: 'Historial detallado de registros y estado oficial de tránsito.', icon: '📝' },
                    { id: 'tecno', title: '3. CERTIFICADO TECNOMECÁNICA', desc: 'Certificación de emisiones y estado mecánico vigente.', icon: '🔧' },
                    { id: 'historial', title: '4. HISTORIAL VEHICULAR', desc: 'Tradición de propietarios, reportes y siniestros.', icon: '📊' }
                 ].map(cert => (
                    <div key={cert.id} className="bg-[#0f172a] border border-white/10 rounded-xl p-3 flex flex-col gap-2 shadow-lg relative overflow-hidden group">
                       <div className="absolute top-1/2 -translate-y-1/2 -right-4 opacity-[0.03] text-8xl pointer-events-none filter grayscale">{cert.icon}</div>
                       <div className="flex justify-between items-start relative z-10">
                          <div className="flex flex-col items-start text-left">
                             <div className="flex items-center gap-1.5 mb-0.5"><span className="text-[16px] drop-shadow-md">{cert.icon}</span><h5 className="font-black text-white text-[13px] tracking-wide">{cert.title}</h5></div>
                             <p className="text-[11.5px] text-slate-400 font-semibold leading-tight">{cert.desc}</p>
                          </div>
                       </div>
                       <button 
                         onClick={() => {
                           setFormVIP({
                             isOpen: true,
                             titulo: cert.title,
                             targetMoto: null,
                             placa: '',
                             cedula: '',
                             nombre: ''
                           });
                         }}
                         className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-yellow-950 font-black text-[11px] uppercase tracking-widest py-2 rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(234,179,8,0.3)] hover:brightness-110 active:scale-95 transition-all mt-0.5"
                       >
                         Solicitar Certificado
                       </button>
                    </div>
                 ))}
                 
                 <div className="bg-black/40 border border-white/5 rounded-xl p-4 mt-3">
                    <p className="text-slate-400 text-[10px] text-center leading-relaxed">
                      El equipo analizará tu solicitud y te presentará el valor antes de procesarla. Tu pago está blindado por MotoPikis.
                    </p>
                 </div>
              </div>

            </div>
          </motion.div>
        )}

        {/* MODAL PERSONALIZADO VIP (FORMULARIO CÉDULA/PLACA) */}
        {formVIP.isOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm p-4 flex flex-col items-center justify-center">
            <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-5 w-full max-w-sm flex flex-col gap-4 shadow-[0_0_30px_rgba(234,179,8,0.15)] relative">
              <button 
                onClick={() => setFormVIP({ ...formVIP, isOpen: false })}
                className="absolute top-2 right-2 p-2 text-slate-400 hover:text-white transition-colors"
                title="Cerrar Formulario VIP"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-2 mb-2 pr-6">
                <ShieldCheck className="w-6 h-6 text-yellow-500" />
                <h3 className="text-white font-black text-sm uppercase tracking-wider">{formVIP.titulo}</h3>
              </div>

              {!formVIP.targetMoto && (
                <div className="flex flex-col gap-3">
                  <p className="text-slate-300 text-[11px] font-medium leading-relaxed">¿Esta consulta pertenece a tu vehículo activo o es para un tercero?</p>
                  <button onClick={() => {
                    const motoData = motosReales.find(m => m.id.toString() === motoActiva);
                    setFormVIP(prev => ({ ...prev, targetMoto: 'mia', placa: motoData?.placa || '', nombre: userNameReal || '' }));
                  }} className="w-full bg-[#1e293b] hover:bg-[#2dd4bf]/20 border border-[#2dd4bf]/30 text-[#2dd4bf] text-xs font-bold py-3 rounded-xl transition-all">
                    ES PARA MI VEHÍCULO ACTUAL
                  </button>
                  <button onClick={() => setFormVIP(prev => ({ ...prev, targetMoto: 'otra' }))} className="w-full bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold py-3 rounded-xl transition-all border border-slate-600">
                    ES PARA OTRO VEHÍCULO
                  </button>
                </div>
              )}

              {formVIP.targetMoto && (
                <div className="flex flex-col gap-3">
                  <p className="text-yellow-500 text-[10px] uppercase font-black tracking-widest text-center border-b border-white/5 pb-2">Datos de Expedición Oficial</p>
                  
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] text-slate-400 uppercase font-bold ml-1">PLACA DEL VEHÍCULO</label>
                    <input 
                      type="text" 
                      value={formVIP.placa} 
                      onChange={e => setFormVIP({...formVIP, placa: e.target.value.toUpperCase()})}
                      className="bg-black/50 border border-slate-700 rounded-lg p-3 text-white font-black text-sm uppercase tracking-widest focus:outline-none focus:border-cyan-500 transition-colors"
                      placeholder="Ej. XYZ123"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] text-slate-400 uppercase font-bold ml-1">DOCUMENTO DE IDENTIDAD</label>
                    <input 
                      type="text" 
                      value={formVIP.cedula} 
                      onChange={e => setFormVIP({...formVIP, cedula: e.target.value.trim().toUpperCase()})}
                      className="bg-black/50 border border-slate-700 rounded-lg p-3 text-white font-black text-sm tracking-widest focus:outline-none focus:border-cyan-500 transition-colors"
                      placeholder="Sin puntos ni espacios"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] text-slate-400 uppercase font-bold ml-1">NOMBRE COMPLETO DEL TITULAR</label>
                    <input 
                      type="text" 
                      value={formVIP.nombre} 
                      onChange={e => setFormVIP({...formVIP, nombre: e.target.value.toUpperCase()})}
                      className="bg-black/50 border border-slate-700 rounded-lg p-3 text-white font-black text-xs uppercase focus:outline-none focus:border-cyan-500 transition-colors"
                      placeholder="Ej. JUAN PEREZ"
                    />
                  </div>

                  <button 
                    onClick={() => {
                      if (!formVIP.placa || !formVIP.cedula || !formVIP.nombre) {
                        mostrarAviso("Por favor completa todos los campos del formulario VIP.", 'error');
                        return;
                      }
                      
                      const placaOk = formVIP.placa.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                      const cedulaOk = formVIP.cedula.trim().toUpperCase();
                      const nombreOk = formVIP.nombre.toUpperCase().trim();
                      const userOk = (userNameAlias || 'Pikis').toUpperCase();

                      const msg = `https://pikishub.com/motopikispro/\n\n🏍 *MOTOPIKIS Pro | VIP* 🏍\n\n📌 *Solicitud de Gestión*\n\n✅ *Certificado:* *${formVIP.titulo.toUpperCase()}*\n\n📋 *DATOS DEL CLIENTE (LIMPIOS):*\n🏍 *Placa:* *${placaOk}*\n💳 *Documento:* *${cedulaOk}*\n🧑 *A nombre de:* *${nombreOk}*\n📱 *Usuario:* *${userOk}*\n\n💰 _Quedo atento a la validación del costo._`;
                      
                      setFormVIP(prev => ({...prev, isOpen: false}));
                      window.open(`https://wa.me/573013310246?text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                    className="w-full mt-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-yellow-950 font-black text-[12px] uppercase tracking-widest py-3 rounded-lg shadow-[0_0_15px_rgba(234,179,8,0.4)] hover:brightness-110 transition-all flex justify-center items-center gap-2"
                  >
                    PROCESAR SOLICITUD
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {pestañaActiva === 'legales' && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className={`absolute inset-0 z-[30] ${themeClass} backdrop-blur-md overflow-y-auto p-4 pt-12 pb-8 sm:p-6 flex flex-col items-center justify-start`}>
            <button onClick={() => setPestañaActiva('')} className="fixed top-2 right-4 z-[60] p-1.5 text-white/50 hover:text-white bg-slate-900/80 hover:bg-danger-red rounded-full backdrop-blur-xl transition-all border border-white/10 shadow-[0_0_20px_rgba(0,0,0,0.8)]">
              <X className="w-4 h-4" />
            </button>
            <div className="w-full max-w-md w-full relative mt-2">
                            <div className="flex justify-between items-end mb-4 px-1">
                <div>

                  <h4 className="text-primary-cyan font-black text-[15px] tracking-wide uppercase">Bóveda Legal</h4>
                  <p className="text-[11px] text-slate-400 font-medium tracking-wide">Tus pólizas y documentos blindados</p>
                </div>
                <button onClick={() => { setTipoModalLegales('OTRO'); setMostrarModalLegales(true); }} className="text-[9px] text-white/80 border border-white/20 uppercase font-bold hover:bg-white/10 transition-colors bg-white/5 px-3 py-2 rounded-xl backdrop-blur-sm shadow-sm">
                  + Añadir Otro
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(docActual?.legales || []).map((leg: any) => {
                  const reqAction = leg.btn;
                  let Watermark = '📄';
                  if (leg.id === 'soat') Watermark = '🛡️';
                  else if (leg.id === 'tecno') Watermark = '⚙️';
                  else if (leg.id === 'impuesto') Watermark = '🏛️';
                  else if (leg.id === 'poliza') Watermark = '🚙';
                  else if (leg.id === 'simit') Watermark = '🚨';
                  let circleColorClass = 'bg-primary-cyan/10 text-primary-cyan'; // Default
                  let bgGradient = 'bg-slate-800/80 border-white/10 shadow-[0_4px_15px_rgba(0,0,0,0.3)]';
                  if (!reqAction) circleColorClass = 'bg-green-500/10 text-green-400';
                  else if (leg.status.includes('Renovar') || leg.status.includes('Cargar') || leg.status.includes('Vincular') || leg.status.includes('AVISO')) circleColorClass = 'bg-gold-cert/10 text-gold-cert';
                  else if (leg.status.includes('RIESGO') || leg.status.includes('COBRO') || leg.status.includes('Expirado')) circleColorClass = 'bg-danger-red/10 text-danger-red';
                  if (leg.border.includes('green') || leg.text.includes('Paz y Salvo')) bgGradient = 'bg-gradient-to-br from-green-500/10 to-emerald-900/20 border-green-500/20 shadow-[0_5px_15px_rgba(16,185,129,0.1)]';
                  else if (leg.border.includes('red') || leg.text.includes('Multas')) bgGradient = 'bg-gradient-to-br from-red-500/10 to-orange-900/20 border-red-500/20 shadow-[0_5px_15px_rgba(239,68,68,0.15)]';
                  else if (leg.border.includes('cyan')) bgGradient = 'bg-gradient-to-br from-blue-500/10 to-slate-900/20 border-blue-500/20 shadow-[0_5px_15px_rgba(59,130,246,0.05)]';
                  else if (leg.border.includes('slate')) bgGradient = 'bg-slate-800/40 border-white/5 shadow-inner opacity-80';
                  return (
                    <div key={leg.id} className={`relative overflow-hidden rounded-2xl p-3 border ${bgGradient} transition-all flex flex-col justify-between min-h-[105px]`}>
                      <div className="flex justify-between items-start w-full relative z-10 mb-2">
                        <h3 className="font-bold text-[12px] text-slate-300 leading-tight w-2/3 pr-1">{leg.title}</h3>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {leg.id !== 'simit' && leg.id !== 'soat' && leg.id !== 'tecno' && leg.id !== 'impuesto' && (
                            <button onClick={(e) => { e.stopPropagation(); handleEliminarDocumento(leg.id, leg.db_id); }} className="p-1 rounded-full bg-danger-red/10 text-danger-red/80 hover:text-danger-red hover:bg-danger-red/20 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${circleColorClass}`}><span className="text-[14px] drop-shadow-md">{Watermark}</span></div>
              </div>
                      </div>
                      <div className="mt-auto relative z-10 flex flex-col gap-2">
                        <p className={`text-[11px] font-medium tracking-wide leading-tight truncate ${!reqAction ? 'text-slate-200' : leg.color || 'text-white'}`}>{leg.text}</p>
                        {reqAction ? (
                          <button onClick={() => { setTipoModalLegales(leg.id); setMostrandoFormMulta(false); setMostrarModalLegales(true); }} className="w-full bg-white/5 hover:bg-white/10 border border-white/50 text-white py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors active:scale-95">{leg.status}</button>
                        ) : (
                          <div className="w-full bg-green-500/5 border border-green-500/20 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest text-center text-green-400">{leg.status || 'OK'}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </motion.div>
        )}

        {pestañaActiva === 'consumibles' && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className={`absolute inset-0 z-[30] ${themeClass} backdrop-blur-md overflow-y-auto p-4 pt-12 pb-8 sm:p-6 flex flex-col items-center justify-start`}>
            <button onClick={() => setPestañaActiva('')} className="fixed top-2 right-4 z-[60] p-1.5 text-white/50 hover:text-white bg-slate-900/80 hover:bg-danger-red rounded-full backdrop-blur-xl transition-all border border-white/10 shadow-[0_0_20px_rgba(0,0,0,0.8)]">
              <X className="w-4 h-4" />
            </button>
            <div className="w-full max-w-md w-full relative mt-2">
                            <div className="flex justify-between items-end mb-4 px-1">
                <div>

                  <h4 className="text-yellow-500 font-black text-[15px] tracking-wide uppercase">Desgaste de Piezas</h4>
                  <p className="text-[11px] text-slate-400 font-medium tracking-wide">Vida útil de componentes clave</p>
                </div>
                <button onClick={() => setMostrarModalConsumible(true)} className="text-[9px] text-white/80 border border-white/20 uppercase font-bold hover:bg-white/10 transition-colors bg-white/5 px-3 py-2 rounded-xl backdrop-blur-sm shadow-sm">
                  + Añadir Repuesto
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(docActual?.consumibles || []).length === 0 ? (
                  <div className="col-span-2 text-center p-6 bg-[#1e293b]/40 rounded-[12px] border border-dashed border-white/10 mt-2"><p className="text-slate-400 text-[11px] font-medium">No hay repuestos registrados en esta moto.</p></div>
                ) : (
                  (docActual?.consumibles || []).map((cons: any, idx: number) => {
                    const warning = cons.percent <= 20;
                    let circleColorClass = warning ? 'bg-danger-red/10 text-danger-red' : (cons.percent <= 50 ? 'bg-amber-500/10 text-amber-500' : 'bg-green-500/10 text-green-400');
                    const valorFmt = cons.valor > 0 ? `$${new Intl.NumberFormat('es-CO').format(cons.valor)}` : '$0';
                    const instFmt = cons.instalacion > 0 ? `$${new Intl.NumberFormat('es-CO').format(cons.instalacion)}` : '$0';
                    let bgGradientC = 'bg-slate-800/40 border-white/5 opacity-80 shadow-inner';
                    if (warning) bgGradientC = 'bg-gradient-to-br from-red-500/10 to-orange-900/20 border-red-500/20 shadow-[0_5px_15px_rgba(239,68,68,0.15)]';
                    else if (cons.percent <= 50) bgGradientC = 'bg-gradient-to-br from-yellow-500/10 to-amber-900/20 border-yellow-500/20 shadow-[0_5px_15px_rgba(234,179,8,0.1)]';
                    else bgGradientC = 'bg-gradient-to-br from-green-500/10 to-emerald-900/20 border-green-500/20 shadow-[0_5px_15px_rgba(16,185,129,0.1)]';
                    return (
                      <div key={cons.id || idx} className={`relative overflow-hidden rounded-2xl p-3 border ${bgGradientC} transition-all flex flex-col justify-between min-h-[150px]`}>
                        <div className="flex justify-between items-start w-full relative z-10 mb-2">
                          <h3 className="font-bold text-[12px] text-slate-300 leading-tight w-2/3 pr-1 capitalize">{cons.name}</h3>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); handleEliminarConsumible(cons.id); }} className="p-1 rounded-full bg-danger-red/10 text-danger-red/80 hover:text-white hover:bg-danger-red transition-colors z-20"><Trash2 className="w-3.5 h-3.5" /></button>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${circleColorClass}`}><span className="text-[14px] drop-shadow-md">⚙️</span></div>
              </div>
                        </div>
                        <div className="flex flex-col gap-1 mb-3 z-10">
                           <div className="flex justify-between items-center w-full"><span className={`text-[9px] font-bold uppercase tracking-widest ${warning ? 'text-danger-red' : 'text-green-400'}`}>Pieza</span><span className={`text-[10px] font-black tracking-widest ${warning ? 'text-danger-red' : 'text-green-400'}`}>{valorFmt}</span></div>
                           <div className="flex justify-between items-center w-full border-t border-white/5 pt-1 mt-0.5"><span className={`text-[9px] font-bold uppercase tracking-widest ${warning ? 'text-danger-red' : 'text-green-400'}`}>Mano de Obra</span><span className={`text-[10px] font-black tracking-widest ${warning ? 'text-danger-red' : 'text-green-400'}`}>{instFmt}</span></div>
                        </div>
                        <div className="w-full bg-slate-900 rounded-full h-1.5 mb-3 overflow-hidden relative z-10 border border-white/5 shadow-inner">
                          <div className={`h-1.5 rounded-full relative transition-all duration-1000 bg-${warning ? 'danger-red' : (cons.percent <= 50 ? 'amber-500' : 'green-500')}`} style={{ width: `${cons.percent}%` }}><div className="absolute inset-0 bg-white/20 w-full h-full animate-pulse"></div></div>
                        </div>
                        <div className="mt-auto relative z-10 flex flex-col gap-2">
                          <p className={`text-[10px] font-bold tracking-wide leading-tight truncate text-white`}>{cons.subtext}</p>
                          <button onClick={(e) => { e.stopPropagation(); handleRenovarConsumible(cons); }} className={`w-full bg-white/5 hover:bg-white/10 border border-white/20 text-white py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors flex justify-center items-center gap-1 active:scale-95`}><RefreshCw className="w-3.5 h-3.5 text-white" /> SUSTITUIR</button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </div>
          </motion.div>
        )}

        {pestañaActiva === 'garantia' && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className={`absolute inset-0 z-[30] ${themeClass} backdrop-blur-md overflow-y-auto p-4 pt-12 pb-8 sm:p-6 flex flex-col items-center justify-start`}>
            <button onClick={() => setPestañaActiva('')} className="fixed top-2 right-4 z-[60] p-1.5 text-white/50 hover:text-white bg-slate-900/80 hover:bg-danger-red rounded-full backdrop-blur-xl transition-all border border-white/10 shadow-[0_0_20px_rgba(0,0,0,0.8)]">
              <X className="w-4 h-4" />
            </button>
            <div className="w-full max-w-md w-full relative mt-2">
                            <div className="flex justify-between items-end mb-4 px-1">
                <div>

                  <h4 className="text-white font-black text-[15px] tracking-wide uppercase">Revisiones Cíclicas</h4>
                  <p className="text-[11px] text-slate-400 font-medium tracking-wide">Historial y garantía de fábrica</p>
                </div>
                <button onClick={() => setMostrarModalGarantia(true)} className="text-[9px] text-white/80 border border-white/20 uppercase font-bold hover:bg-white/10 transition-colors bg-white/5 px-3 py-2 rounded-xl backdrop-blur-sm shadow-sm">
                  + Añadir Cita
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(docActual?.garantia || []).length === 0 ? (
                  <div className="col-span-2 text-center p-6 bg-[#1e293b]/40 rounded-[12px] border border-dashed border-white/10 mt-2"><p className="text-slate-400 text-[11px] font-medium">No hay historial de garantía o citas registradas en esta moto.</p></div>
                ) : (
                  (docActual?.garantia || []).map((gar: any) => {
                    const reqAction = !gar.done;
                    let circleColorClass = gar.done ? 'bg-slate-500/10 text-slate-400' : (gar.past ? 'bg-danger-red/10 text-danger-red' : 'bg-green-500/10 text-green-400');
                    let bgGradientG = 'bg-slate-800/40 border-white/5 opacity-80 shadow-inner';
                    if (gar.done) bgGradientG = 'bg-slate-800/30 border-white/5 opacity-50 grayscale transition-all';
                    else if (gar.past) bgGradientG = 'bg-gradient-to-br from-red-500/10 to-orange-900/20 border-red-500/20 shadow-[0_5px_15px_rgba(239,68,68,0.15)]';
                    else bgGradientG = 'bg-gradient-to-br from-green-500/10 to-emerald-900/20 border-green-500/20 shadow-[0_5px_15px_rgba(16,185,129,0.1)]';
                    return (
                      <div key={gar.id} className={`relative overflow-hidden rounded-2xl p-3 border ${bgGradientG} transition-all flex flex-col justify-between min-h-[140px]`}>
                        <div className="flex justify-between items-start w-full relative z-10 mb-2">
                          <h3 className={`font-bold text-[12px] leading-tight w-2/3 pr-1 capitalize ${gar.done ? 'line-through text-slate-500' : 'text-slate-300'}`}>{gar.title}</h3>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); abrirEdicionGarantia(gar); }} className="p-1 rounded-full bg-blue-500/10 text-blue-400 hover:text-white hover:bg-blue-500 transition-colors z-20"><Edit3 className="w-3.5 h-3.5" /></button>
                            <button onClick={(e) => { e.stopPropagation(); handleEliminarDocumento(gar.title, gar.id); }} className="p-1 rounded-full bg-danger-red/10 text-danger-red/80 hover:text-white hover:bg-danger-red transition-colors z-20"><Trash2 className="w-3.5 h-3.5" /></button>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${circleColorClass}`}><span className="text-[14px] drop-shadow-md">🛠️</span></div>
              </div>
                        </div>
                        <div className="flex flex-col gap-1 mb-3 z-10 mt-1">
                           <div className="flex justify-between items-center w-full"><span className={`text-[9px] font-bold uppercase tracking-widest ${gar.done ? 'text-slate-500' : (gar.past ? 'text-danger-red' : 'text-green-400')}`}>Fecha</span><span className={`text-[10px] font-black tracking-widest ${gar.done ? 'text-slate-500 line-through' : (gar.past ? 'text-danger-red' : 'text-green-400')}`}>{gar.text.replace('Fecha: ','')}</span></div>
                           {gar.valorInfo && (
                           <div className="flex justify-between items-center w-full border-t border-white/5 pt-1 mt-0.5"><span className={`text-[9px] font-bold uppercase tracking-widest ${gar.done ? 'text-slate-500' : (gar.past ? 'text-danger-red' : 'text-green-400')}`}>Valor</span><span className={`text-[10px] font-black tracking-widest ${gar.done ? 'text-slate-500 line-through' : (gar.past ? 'text-danger-red' : 'text-green-400')}`}>{gar.valorInfo}</span></div>
                           )}
                        </div>
                        <div className="mt-auto relative z-10 flex flex-col gap-1.5">
                          <button onClick={(e) => { e.stopPropagation(); handleToggleGarantia(gar.id, gar.done); }} className={`w-full bg-white/5 hover:bg-white/10 border ${gar.done ? 'border-green-500/30 text-green-500' : (gar.past ? 'border-white/20 text-white' : 'border-white/50 text-white')} py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors flex justify-center items-center gap-1 active:scale-95`}>
                             <RefreshCw className={`w-3.5 h-3.5 ${gar.past ? 'text-white' : ''}`} /> {gar.done ? 'COMPLETADO' : (gar.past ? 'VENCIDO' : 'MARCAR HECHO')}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </div>
          </motion.div>
        )}

        {pestañaActiva === 'alertas' && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className={`absolute inset-0 z-[30] ${themeClass} backdrop-blur-md overflow-y-auto p-4 pt-12 pb-8 sm:p-6 flex flex-col items-center justify-start`}>
            <button onClick={() => setPestañaActiva('')} className="fixed top-2 right-4 z-[60] p-1.5 text-white/50 hover:text-white bg-slate-900/80 hover:bg-danger-red rounded-full backdrop-blur-xl transition-all border border-white/10 shadow-[0_0_20px_rgba(0,0,0,0.8)]">
              <X className="w-4 h-4" />
            </button>
            <div className="w-full max-w-md w-full relative mt-2">
                            <div className="flex justify-between items-end mb-4 px-1">
                <div>

                  <h4 className="text-danger-red font-black text-[15px] tracking-wide uppercase">Centro de Alertas</h4>
                  <p className="text-[11px] text-slate-400 font-medium tracking-wide">Tareas críticas que requieren tu atención</p>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {(() => {
                   const dA = docActual || { legales: [], consumibles: [], garantia: [] };
                   const alertasL = (dA.legales || []).filter((l: any) => l.status.includes('RENOVAR') || l.status.includes('PAGAR') || l.status.includes('Expirado'));
                   const alertasC = (dA.consumibles || []).filter((c: any) => c.percent <= 20);
                   const alertasG = (dA.garantia || []).filter((g: any) => g.past && !g.done);
                   const total = alertasL.length + alertasC.length + alertasG.length;
                   if (total === 0) {
                     return (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 text-center shadow-[0_0_20px_rgba(34,197,94,0.1)]">
                           <ShieldCheck className="w-12 h-12 text-green-500 mx-auto mb-2" />
                           <h3 className="text-white font-bold text-lg mb-1">¡Todo en Orden!</h3>
                           <p className="text-slate-400 text-xs">No tienes avisos vehiculares críticos ni pendientes urgentes.</p>
                        </div>
                     );
                   }
                   return (
                     <>
                       {alertasL.map((l: any) => <div key={l.id} className="bg-danger-red/10 border-l-4 border-l-danger-red rounded-lg p-3 flex justify-between items-center shadow-sm"><div><span className="text-[10px] tracking-widest text-danger-red uppercase font-black">LEGAL VENCIDO</span><p className="text-white font-bold text-[13px]">{l.title}</p></div><button onClick={() => { setPestañaActiva('legales'); }} className="text-[10px] tracking-widest bg-danger-red/20 text-white px-4 py-2 rounded-lg uppercase font-black active:scale-95 shadow-sm leading-none flex items-center justify-center">REVISAR</button></div>)}
                       {alertasC.map((c: any) => <div key={c.id} className="bg-orange-500/10 border-l-4 border-l-orange-500 rounded-lg p-3 flex justify-between items-center shadow-sm"><div><span className="text-[10px] tracking-widest text-orange-500 uppercase font-black">DESGASTE CRÍTICO</span><p className="text-white font-bold text-[13px]">{c.name}</p></div><button onClick={() => { setPestañaActiva('consumibles'); }} className="text-[10px] tracking-widest bg-orange-500/20 text-white px-4 py-2 rounded-lg uppercase font-black active:scale-95 shadow-sm leading-none flex items-center justify-center">REVISAR</button></div>)}
                       {alertasG.map((g: any) => <div key={g.id} className="bg-blue-500/10 border-l-4 border-l-blue-500 rounded-lg p-3 flex justify-between items-center shadow-sm"><div><span className="text-[10px] tracking-widest text-blue-500 uppercase font-black">CITA VENCIDA</span><p className="text-white font-bold text-[13px]">{g.title}</p></div><button onClick={() => { setPestañaActiva('garantia'); }} className="text-[10px] tracking-widest bg-blue-500/20 text-white px-4 py-2 rounded-lg uppercase font-black active:scale-95 shadow-sm leading-none flex items-center justify-center">REVISAR</button></div>)}
                     </>
                   );
                })()}
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODAL DEL CÓDIGO QR */}
      <AnimatePresence>
        {mostrarQR && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-[150] bg-black/90 backdrop-blur-xl flex flex-col justify-center items-center p-5"
          >
            <button onClick={() => setMostrarQR(false)} className="absolute top-8 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-full z-[200] text-white transition-all shadow-[0_0_20px_rgba(0,0,0,0.5)]">
              <X className="w-6 h-6" />
            </button>
            
            <div id="certificado-oro-card" className="bg-gradient-to-br from-[#1e293b] to-black border border-gold-cert/50 rounded-3xl p-6 shadow-[0_0_50px_rgba(251,191,36,0.3)] text-center w-full max-w-[340px] relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-yellow-400 via-yellow-600 to-yellow-400"></div>
              
              <div className="flex justify-center items-center gap-2 mb-2 mt-1">
                <Crown className="w-8 h-8 text-gold-cert" />
              </div>
              <h2 className="font-black text-[18px] text-white uppercase tracking-widest mb-1">CERTIFICADO ORO</h2>
              <div className="bg-green-500/20 border border-green-500/30 rounded-full px-3 py-1 inline-block mb-5">
                 <p className="text-green-400 text-[10px] font-black uppercase tracking-widest">✅ 100% Legal Al Día</p>
              </div>
              
              <div className="bg-white p-3 rounded-2xl mx-auto inline-block shadow-[0_0_20px_rgba(255,255,255,0.2)] mb-4">
                <QRCodeCanvas value={qrUrl} size={160} />
              </div>
              
              <div className="bg-black/50 py-3 px-4 rounded-xl border border-white/5 mx-auto mb-1">
                <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest mb-0.5">Hash de Seguridad Alfanumérico</p>
                <p className="text-gold-cert font-black text-sm tracking-widest">{hash}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 w-full max-w-[340px]">
              <button 
                onClick={async (e) => {
                   const btn = e.currentTarget;
                   const originalHtml = btn.innerHTML;
                   btn.innerHTML = `<span class="animate-pulse">📸 ENFOCANDO...</span>`;
                   try {
                      const element = document.getElementById('certificado-oro-card');
                      if (element) {
                         // Destello de cámara instantáneo
                         const flash = document.createElement('div');
                         flash.className = "fixed inset-0 bg-white z-[9999] pointer-events-none transition-opacity duration-500 ease-out";
                         document.body.appendChild(flash);
                         setTimeout(() => { flash.style.opacity = '0'; }, 30);
                         setTimeout(() => { document.body.removeChild(flash); }, 600);

                         // toJpeg de html-to-image soporta oklab y CSS moderno nativamente
                         const dataUrl = await toJpeg(element, { quality: 1, pixelRatio: 3, backgroundColor: '#0f172a' });
                         const a = document.createElement('a');
                         a.href = dataUrl;
                         a.download = `Certificado_Oro_${hash}.jpg`;
                         a.click();
                         
                         btn.innerHTML = `✅ ¡GUARDADO!`;
                         btn.classList.add('bg-green-600', 'text-white', 'border-green-500');
                         setTimeout(() => { 
                             btn.innerHTML = originalHtml; 
                             btn.classList.remove('bg-green-600', 'text-white', 'border-green-500');
                         }, 3000);
                      }
                   } catch(e: any) {
                      console.error("html-to-image error:", e);
                      mostrarAviso("Error al capturar la credencial: " + (e?.message || e?.toString() || 'Ignorado'), 'error');
                      btn.innerHTML = originalHtml;
                   }
                }}
                className="w-full bg-slate-800 text-slate-200 border border-slate-600 font-bold text-[10px] uppercase tracking-widest py-3 hover:bg-slate-700 active:scale-95 transition-all rounded-xl shadow-xl flex items-center justify-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                Descargar IMG
              </button>

              <button 
                onClick={() => {
                  const motoData = motosReales.find(m => m.id.toString() === motoActiva);
                  const placaAct = motoData?.placa?.toUpperCase() || 'mi moto';
                  const msg = `🦊🏍️ Mi moto *${placaAct}* está amparada oficialmente por *MOTOPIKIS Pro* como *100% LEGAL AL DÍA* (Membresía Oro).\n\n🛡️ Verifica la matrícula aquí:\n${qrUrl}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                }}
                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-yellow-950 font-black text-[10px] uppercase tracking-widest py-3 hover:scale-[1.02] active:scale-95 transition-all rounded-xl shadow-[0_0_15px_rgba(234,179,8,0.3)] flex items-center justify-center gap-1.5"
              >
                <Copy className="w-4 h-4" />
                Compartir
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODAL DE ACTUALIZAR KM */}
      <AnimatePresence>
        {mostrarModalKm && (
          <div className="fixed inset-0 z-[100] flex flex-col justify-end">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setMostrarModalKm(false)}
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`relative w-full ${themeClass} rounded-t-3xl p-6 shadow-[0_-10px_40px_rgba(0,168,255,0.2)] border-t border-primary-cyan/20 pb-8`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-1 rounded-full bg-white/20 mx-auto mb-6"></div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-xl text-white">Actualizar Kilometraje</h3>
                <button onClick={() => setMostrarModalKm(false)} className="bg-slate-800 p-2 rounded-full text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-[14px] text-slate-400 mb-6 font-medium leading-tight">
                Digita el kilometraje actual que muestra el tablero de tu moto. Esto recalculará instantáneamente el desgaste de llantas, aceite y pastillas.
              </p>

              <div className="relative mb-4">
                <input 
                  type="text" 
                  inputMode="numeric"
                  value={nuevoKm}
                  onChange={(e) => setNuevoKm(formatMiles(e.target.value))}
                  className="w-full bg-black/50 border border-white/10 text-white text-3xl font-black rounded-xl py-4 px-4 focus:outline-none focus:border-primary-cyan focus:ring-1 focus:ring-primary-cyan transition-all text-center placeholder-slate-700"
                  placeholder="Ej: 15.300"
                  autoFocus
                />
                <div className="absolute top-1/2 -translate-y-1/2 right-5 text-slate-500 font-bold tracking-widest text-sm">KM</div>
              </div>

              <button 
                onClick={() => {
                  if (nuevoKm) {
                    const kmVal = parseInt(nuevoKm.replace(/\D/g, ''), 10);
                    if (kmVal > (metricasMotos[motoActiva]?.km || 0)) {
                      setMetricasMotos(prev => ({ ...prev, [motoActiva]: { ...prev[motoActiva], km: kmVal } }));
                      supabase.from('vehiculos').update({ km_actual: kmVal }).eq('id', motoActiva).then();
                      mostrarAviso('Kilometraje sincronizado y actualizado correctamente.', 'success');
                    } else {
                      mostrarAviso('El nuevo kilometraje debe ser mayor al actual registrado.', 'error');
                      return;
                    }
                  }
                  setMostrarModalKm(false);
                  setNuevoKm('');
                }}
                className="w-full py-3.5 bg-primary-cyan text-bg-dark rounded-xl font-black uppercase tracking-widest text-[14px] hover:bg-cyan-400 transition-colors shadow-[0_0_15px_rgba(0,168,255,0.4)] mt-2"
              >
                Guardar y Recalcular
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL DE COMBUSTIBLE */}
      <AnimatePresence>
        {mostrarModalGas && (
          <div className="fixed inset-0 z-[100] flex flex-col justify-end">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setMostrarModalGas(false)}
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`relative w-full ${themeClass} rounded-t-3xl p-6 shadow-[0_-10px_40px_rgba(0,168,255,0.2)] border-t border-primary-cyan/20 pb-8`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-1 rounded-full bg-white/20 mx-auto mb-6"></div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-xl text-white">Registrar Tanqueo ⛽</h3>
                <button onClick={() => setMostrarModalGas(false)} className="bg-slate-800 p-2 rounded-full text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-[14px] text-slate-400 mb-6 font-medium leading-tight">
                Añade el valor del repostaje. La app calculará automáticamente tu rendimiento por galón.
              </p>

              <div className="mb-4">
                <label className="text-[13px] text-slate-400 font-bold mb-1 block">Valor tanqueo</label>
                <div className="relative">
                  <input 
                    type="text" 
                    inputMode="numeric"
                    value={valorGas}
                    onChange={(e) => setValorGas(formatMiles(e.target.value))}
                    className="w-full bg-black/50 border border-white/10 text-green-400 text-2xl font-black rounded-xl py-3.5 pl-4 pr-16 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all placeholder-green-400/20 shadow-inner"
                    placeholder="Ej: 25.000"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex gap-3 mb-6">
                <div className="flex-1">
                  <label className="text-[13px] text-slate-400 font-bold mb-1 block">Odómetro actual</label>
                  <div className="relative">
                    <input 
                      type="text" 
                      inputMode="numeric"
                      value={kmGas}
                      onChange={(e) => setKmGas(formatMiles(e.target.value))}
                      className="w-full bg-black/50 border border-white/10 text-white text-lg font-black rounded-xl py-3 pl-4 pr-10 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all placeholder-slate-700"
                      placeholder="Ej: 15.300"
                    />
                    <div className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-500 font-bold tracking-widest text-[9px]">KM</div>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-[13px] text-slate-400 font-bold mb-1 block">Volumen (gal)</label>
                  <div className="relative">
                    <input 
                      type="number" 
                      value={galonesGas}
                      onChange={(e) => setGalonesGas(e.target.value)}
                      className="w-full bg-black/50 border border-white/10 text-white text-lg font-black rounded-xl py-3 pl-4 pr-10 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all placeholder-slate-700 text-center"
                      placeholder="1.5"
                    />
                    <div className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-500 font-bold tracking-widest text-[9px]">GAL</div>
                  </div>
                </div>
              </div>

              <button 
                onClick={async () => {
                  if (kmGas && galonesGas) {
                    const kmVal = parseInt(kmGas.replace(/\D/g, ''), 10);
                    const galVal = parseFloat(galonesGas.replace(',', '.'));
                    const valorVal = parseInt(valorGas.replace(/\D/g, ''), 10) || 0;
                    const oldKm = metricasMotos[motoActiva]?.km || 0;
                    
                    if (kmVal > oldKm && galVal > 0) {
                      const nuevoRendimiento = oldKm === 0 ? 0 : Math.round((kmVal - oldKm) / galVal);
                      
                      // 1. Guardar en Supabase (Historial)
                      const { error: hErr } = await supabase.from('historial_combustible').insert([{
                        vehiculo_id: motoActiva,
                        galones: galVal,
                        valor_pagado: valorVal,
                        km_odometro: kmVal,
                        fecha_repostaje: new Date().toISOString()
                      }]);

                      if (hErr) {
                         mostrarAviso("Error al sincronizar tanqueo: " + hErr.message, 'error');
                         return;
                      }

                      // 2. Actualizar Odómetro en Tabla Vehículos
                      await supabase.from('vehiculos').update({ km_actual: kmVal }).eq('id', motoActiva);
                      
                      // 3. Actualizar Estado Local
                      setMetricasMotos(prev => ({ ...prev, [motoActiva]: { km: kmVal, rendimiento: nuevoRendimiento } }));
                      setHistorialGas(prev => {
                        const arr = prev[motoActiva] || [];
                        const nuevoTanqueo = { 
                          fecha: new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', ''), 
                          valor: valorGas || '0', 
                          km: kmVal, 
                          rendimiento: nuevoRendimiento, 
                          galones: galVal 
                        };
                        return { ...prev, [motoActiva]: [nuevoTanqueo, ...arr] };
                      });

                      if (oldKm === 0) {
                        mostrarAviso('¡Primer tanqueo sincronizado con la nube!', 'success');
                      } else {
                        mostrarAviso(`¡Tanqueo sincronizado exitosamente! Rendimiento calculado: ${nuevoRendimiento} Km/G.`, 'success');
                      }
                    } else if (kmVal <= oldKm) {
                      mostrarAviso('Odómetro inválido: el kilometraje debe ser mayor al actual.', 'error');
                      return;
                    }
                    
                    setTriggerSync(s => s + 1);
                    setMostrarModalGas(false);
                    setKmGas('');
                    setValorGas('');
                    setGalonesGas('');
                  } else {
                    mostrarAviso('Por favor, completa el Odómetro y el Volumen (gal) para continuar.', 'error');
                  }
                }}
                className="w-full py-3.5 bg-green-500 text-green-950 rounded-xl font-black uppercase tracking-widest text-[14px] hover:bg-green-400 transition-colors shadow-[0_0_15px_rgba(34,197,94,0.3)] mt-2"
              >
                Guardar Tanqueo
              </button>
              
              <button 
                onClick={() => setMostrarHistorialGas(!mostrarHistorialGas)}
                className="w-full mt-4 flex items-center justify-center gap-2 py-3 border border-white/10 text-slate-300 rounded-lg font-bold text-[14px] sm:text-[15px] hover:bg-white/5 transition-colors shadow-sm"
              >
                <History className="w-4 h-4" />
                {mostrarHistorialGas ? 'Ocultar Histórico' : 'Ver Últimos 5 Tanqueos'}
              </button>

              <AnimatePresence>
                {mostrarHistorialGas && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }} 
                    animate={{ height: "auto", opacity: 1 }} 
                    exit={{ height: 0, opacity: 0 }} 
                    className="overflow-hidden mt-4"
                  >
                    <div className="bg-black/30 rounded-xl border border-white/5 p-3 space-y-2 mb-3">
                      <div className="flex justify-between text-[9px] text-slate-500 font-black uppercase pb-1 border-b border-white/5 px-1">
                        <span>Fecha</span>
                        <span>Info</span>
                        <span className="text-right">Consumo</span>
                      </div>
                      {(historialGas[motoActiva] || []).slice(0, 5).map((t, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white/5 p-2 rounded-lg text-white text-[10px] font-medium border border-white/5">
                          <span className="text-slate-400 capitalize whitespace-nowrap">{t.fecha}</span>
                          <span className="text-center w-full grow px-2 leading-tight">
                            <b className="text-xs tracking-wider">${t.valor}</b><br/>
                            <span className="text-[8px] text-slate-500 font-mono">{new Intl.NumberFormat('es-CO').format(t.km)} km</span>
                          </span>
                          <span className="text-green-400 font-bold whitespace-nowrap bg-green-400/10 px-1.5 py-0.5 rounded text-[11px]">{t.rendimiento} Km/G</span>
                        </div>
                      ))}
                    </div>

                    <button 
                      onClick={exportarHistorialPDF}
                      className="w-full flex items-center justify-center gap-2 bg-slate-800 border border-slate-700 hover:border-slate-500 text-slate-300 py-2.5 rounded-lg shadow-md transition-all group"
                    >
                      <FileText className="w-4 h-4 text-rose-500 group-hover:scale-110 transition-transform" />
                      <span className="font-bold text-[11px] tracking-wide">EXPORTAR HISTORIAL PDF</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL DE LEGALES (SIMIT / SOAT) */}
      <AnimatePresence>
        {mostrarModalLegales && (
          <div className="fixed inset-0 z-[100] flex flex-col justify-end">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setMostrarModalLegales(false)}
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`relative w-full ${themeClass} rounded-t-3xl p-6 shadow-[0_-10px_40px_rgba(0,168,255,0.2)] border-t border-primary-cyan/20 pb-8 max-h-[90vh] overflow-y-auto`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-1 rounded-full bg-white/20 mx-auto mb-6"></div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg text-white">
                  {tipoModalLegales === 'simit' ? 'Chequeo Preventivo SIMIT 🚔' : 'Actualizar Documento 📄'}
                </h3>
                <button 
                  onClick={() => setMostrarModalLegales(false)} 
                  className="bg-slate-800 p-1.5 rounded-full text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* ASISTENTE DE COPIADO MAGICO MOTOPIKIS PRO */}
              <div className="bg-slate-800/80 border border-gold-cert/30 rounded-xl mb-5 flex divide-x divide-white/10 shadow-[0_5px_20px_rgba(0,0,0,0.3)]">
                 <div className="flex-1 flex flex-col items-center justify-center relative p-3 group">
                   <span className="text-[9px] text-slate-400 font-bold tracking-widest uppercase mb-1">PLACA MOTO</span>
                   <span className="text-white font-black text-[15px] leading-none mb-2">{motosReales.find((m:any) => m.id.toString() === motoActiva)?.placa || 'N/A'}</span>
                   <button onClick={() => { navigator.clipboard.writeText(motosReales.find((m:any) => m.id.toString() === motoActiva)?.placa || ''); mostrarAviso('¡Placa copiada! Lista para Pegar.', 'info'); }} className="flex items-center gap-1 px-3 py-1.5 bg-primary-cyan/20 text-primary-cyan text-[10px] font-black uppercase rounded-lg hover:bg-primary-cyan/40 active:scale-95 transition-all">
                     <Copy className="w-3 h-3" /> COPIAR
                   </button>
                 </div>
                 <div className="flex-1 flex flex-col items-center justify-center relative p-3 group">
                   <span className="text-[9px] text-slate-400 font-bold tracking-widest uppercase mb-1">DOC. REPORTE</span>
                   <span className="text-white font-black text-[15px] leading-none mb-2">{Storage.get('motopikis_cedula') || Storage.get('motopikis_identificacion') || 'N/A'}</span>
                   <button onClick={() => { navigator.clipboard.writeText(Storage.get('motopikis_cedula') || Storage.get('motopikis_identificacion') || ''); mostrarAviso('¡Documento copiado! Listo para Pegar.', 'info'); }} className="flex items-center gap-1 px-3 py-1.5 bg-primary-cyan/20 text-primary-cyan text-[10px] font-black uppercase rounded-lg hover:bg-primary-cyan/40 active:scale-95 transition-all">
                     <Copy className="w-3 h-3" /> COPIAR
                   </button>
                 </div>
              </div>

              {tipoModalLegales === 'simit' ? (
                mostrandoFormMulta ? (
                  <motion.div initial={{opacity:0}} animate={{opacity:1}}>
                    <p className="text-[14px] text-slate-400 mb-3 font-medium">Registra el valor para calcular tus descuentos por pronto pago.</p>
                    <label className="text-[13px] text-slate-400 font-bold mb-2 mt-2 block">Valor de la multa ($)</label>
                    <input type="number" placeholder="Ej: 345000" className="w-full bg-black/50 border border-white/10 text-white text-base font-bold rounded-lg py-3 px-3 mb-3 focus:border-danger-red outline-none" />
                    <label className="text-[13px] text-slate-400 font-bold mb-2 mt-2 block">Fecha límite (50% descuento)</label>
                    <input type="date" className="w-full bg-black/50 border border-white/10 text-slate-300 text-base font-bold rounded-lg py-3 px-3 mb-4 focus:border-danger-red outline-none" />
                    <button onClick={() => handleGuardarSimit('MULTA')} className="w-full py-2 bg-danger-red text-white rounded-lg font-black uppercase tracking-wider text-xs py-3 hover:bg-red-500 shadow-[0_0_15px_rgba(239,68,68,0.4)]">
                      Guardar Deuda
                    </button>
                  </motion.div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p className="text-[13px] text-slate-300 font-medium leading-relaxed bg-slate-800/50 p-3 rounded-xl border border-white/5">
                      Evita cobros coactivos o embargos sorpresa. Consulta directamente en la base de datos nacional y actualiza tu estado aquí abajo.
                    </p>
                    
                    {config.code === 'CO' && (
                      <a href="https://fcm.org.co/simit/" target="_blank" rel="noreferrer" className="w-full py-3 bg-blue-600/20 border border-blue-500/50 text-blue-400 rounded-lg font-black uppercase tracking-wider text-xs hover:bg-blue-600/30 transition-colors flex justify-center items-center gap-2 mb-2">
                        Ir al portal oficial SIMIT
                      </a>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 mt-2 pt-4 border-t border-white/10">
                      <button onClick={() => handleGuardarSimit('OK')} className="py-2.5 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg font-bold text-xs hover:bg-green-500/20 active:scale-95 transition-all">
                        ✅ Estoy a Paz y Salvo
                      </button>
                      <button onClick={() => setMostrandoFormMulta(true)} className="py-2.5 bg-danger-red/10 border border-danger-red/30 text-danger-red rounded-lg font-bold text-xs hover:bg-danger-red/20 active:scale-95 transition-all">
                        🚨 Registrar Multa
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <div className="flex flex-col gap-3">
                   <p className="text-[13px] text-slate-400 font-medium leading-tight mb-3">
                    Revisa en físico o consulta digitalmente para ingresar la fecha de expiración. MOTOPIKIS Pro te avisará 15 días antes.
                   </p>
                   
                   {config.code === 'CO' && (
                     <a href="https://www.runt.com.co/consultaCiudadana/#/consultaVehiculo" target="_blank" rel="noreferrer" className="w-full py-3 bg-[#0a58ca]/20 border border-[#0a58ca]/50 text-blue-400 rounded-lg font-black uppercase tracking-wider text-[11px] hover:bg-[#0a58ca]/30 transition-colors flex justify-center items-center gap-2 mb-4 shadow-lg active:scale-95">
                        Consultar estado en el Búnker de Tránsito
                     </a>
                   )}
                   {tipoModalLegales === 'OTRO' && (
                     <>
                       <label className="text-[13px] text-slate-400 font-bold mb-2 mt-2 block">Nombre del Documento</label>
                       <input 
                         type="text" 
                         value={nombreOtroDoc}
                         onChange={(e) => setNombreOtroDoc(formatTitleCase(e.target.value))}
                         placeholder="Ej: Tarjeta Propiedad, RTM Extra..."
                         className="w-full bg-black/50 border border-white/10 text-white text-base font-bold rounded-lg py-3.5 px-3 mb-3 focus:border-primary-cyan outline-none" 
                       />
                     </>
                   )}
                   <label className="text-[13px] text-slate-400 font-bold mb-2 mt-2 block">Fecha de vencimiento</label>
                   <input 
                     type="date" 
                     value={fechaLegal}
                     onChange={(e) => setFechaLegal(e.target.value)}
                     className="w-full bg-slate-900 border-2 border-slate-500 text-white text-base font-bold rounded-xl py-3.5 px-4 mb-4 focus:border-primary-cyan outline-none text-center shadow-inner [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert" 
                   />
                   <button onClick={handleGuardarDocumento} className="w-full py-2.5 bg-primary-cyan text-bg-dark rounded-lg font-black uppercase tracking-wider text-xs py-3 hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,168,255,0.4)]">
                     Vincular y Activar Alerta
                   </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>


      {/* MODAL AÑADIR REPUESTO */}
      <AnimatePresence>
        {mostrarModalConsumible && (
          <div className="fixed inset-0 z-[100] flex flex-col justify-end">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
              onClick={cerrarModalConsumibles}
            />
            <motion.div 
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`relative w-full ${themeClass} rounded-t-[30px] p-6 shadow-[0_-15px_50px_rgba(0,168,255,0.15)] border-t border-primary-cyan/30 flex flex-col max-h-[85vh]`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-1.5 rounded-full bg-white/20 mx-auto mb-5 shrink-0"></div>
              
              <div className="flex justify-between items-center mb-2 shrink-0">
                <h3 className="font-black text-[15px] sm:text-lg text-white tracking-tight flex items-center gap-2">
                  ⚙️ {editandoConsumibleId ? 'Sustituir / Editar Pieza' : 'Instalación de Pieza Nueva'}
                </h3>
                <button onClick={cerrarModalConsumibles} className="bg-slate-800 p-2 rounded-full text-slate-400 hover:text-white transition-colors shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[13px] text-slate-400 mb-5 font-medium leading-relaxed shrink-0 border-b border-white/10 pb-4">
                Registra la pieza instalada hoy. El sistema deducirá su desgaste restante conforme vayas reportando nuevos Kilómetros recorridos.
              </p>

              <div className="flex-1 overflow-y-auto pr-2 pb-6 space-y-4 scrollbar-hide">
                <div>
                  <label className="text-xs text-text-muted font-black uppercase tracking-widest block mb-1">Nombre del Repuesto</label>
                  <input 
                    type="text" 
                    value={nuevoConsumibleNombre}
                    onChange={(e) => setNuevoConsumibleNombre(formatTitleCase(e.target.value))}
                    className="w-full bg-black/60 border border-slate-700 text-white font-black text-base rounded-xl py-3 px-4 focus:border-primary-cyan outline-none text-center tracking-widest shadow-inner placeholder-slate-600"
                    placeholder="Ej: Kit de Arrastre, Aceite, Llantas"
                  />
                </div>
                
                <div>
                  <label className="text-[13px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Duración Estimada Vida Útil</label>
                  <div className="relative">
                    <input 
                      type="text"
                      inputMode="numeric"
                      value={nuevoConsumibleDuracion}
                      onChange={(e) => setNuevoConsumibleDuracion(formatMiles(e.target.value))}
                      className="w-full bg-black/40 border border-slate-700 text-white font-bold text-lg rounded-lg py-3 pl-4 pr-12 focus:border-primary-cyan outline-none placeholder-slate-600 text-center"
                      placeholder="Ej: 5.000"
                    />
                    <div className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-500 font-bold tracking-widest text-[9px]">KM</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="text-[11px] text-white/60 font-bold uppercase tracking-wider mb-1 block">Valor Pieza</label>
                    <div className="relative">
                      <div className="absolute top-1/2 -translate-y-1/2 left-3 text-slate-500 font-bold">$</div>
                      <input 
                        type="text" 
                        inputMode="numeric"
                        value={nuevoConsumibleValor}
                        onChange={(e) => setNuevoConsumibleValor(formatMiles(e.target.value))}
                        className="w-full bg-black/30 border border-white/10 text-white rounded-lg pl-7 pr-3 py-3 outline-none focus:border-primary-cyan text-sm shadow-inner"
                        placeholder="25.000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-white/60 font-bold uppercase tracking-wider mb-1 block">Mano de Obra</label>
                    <div className="relative">
                      <div className="absolute top-1/2 -translate-y-1/2 left-3 text-slate-500 font-bold">$</div>
                      <input 
                        type="text" 
                        inputMode="numeric"
                        value={nuevoConsumibleManoObra}
                        onChange={(e) => setNuevoConsumibleManoObra(formatMiles(e.target.value))}
                        className="w-full bg-black/30 border border-white/10 text-white rounded-lg pl-7 pr-3 py-3 outline-none focus:border-primary-cyan text-sm shadow-inner"
                        placeholder="10.000"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2 shrink-0">
                <button 
                  onClick={handleGuardarConsumible}
                  className="w-full bg-gradient-to-r from-primary-cyan to-blue-600 text-white rounded-xl font-black uppercase tracking-widest text-[14px] py-4 hover:opacity-90 active:scale-95 transition-all shadow-[0_0_20px_rgba(0,168,255,0.4)]"
                >
                  {editandoConsumibleId ? 'Confirmar Sustitución' : 'Registrar y Rastrear Pieza'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL DE REGISTRO NUEVA MOTO EN BÓVEDA (SUPABASE) */}
      <AnimatePresence>
        {mostrarModalNuevaMoto && (
          <div className="fixed inset-0 z-[100] flex flex-col">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-xl"
              onClick={() => setMostrarModalNuevaMoto(false)}
            />
            <motion.div 
              initial={{ y: "20%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: "20%", opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`relative w-full ${themeClass} shadow-[0_0_50px_rgba(0,168,255,0.15)] flex flex-col h-full`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-6 pb-2 shrink-0">
                <h3 className="font-black text-lg text-white tracking-tight flex items-center gap-2">
                  <ShieldCheck className="text-primary-cyan w-5 h-5"/> Legalidad del Vehículo
                </h3>
                <button onClick={() => setMostrarModalNuevaMoto(false)} className="bg-slate-800 p-2 rounded-full text-slate-400 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="px-6 text-[13px] text-slate-400 mb-2 font-medium leading-relaxed shrink-0 border-b border-white/10 pb-3">
                Ingresa los datos exactos de tus documentos. Estos datos quedarán asegurados en tu bóveda legal.
              </p>

              <div className="flex-1 overflow-y-auto pr-2 pb-6 space-y-4 scrollbar-hide">
                {/* Placa con Validación en Tiempo Real */}
                <div>
                  <label className="text-xs text-text-muted font-black uppercase tracking-widest block mb-1">Placa Oficial</label>
                  {(() => {
                    const limpia = nuevaMotoPlaca.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    const esValida = config.plateRegEx.test(limpia);
                    const estaVacia = limpia.length === 0;
                    
                    return (
                      <input 
                        type="text" 
                        value={nuevaMotoPlaca} 
                        onChange={(e) => setNuevaMotoPlaca(formatPlacaMoto(e.target.value))} 
                        className={`w-full bg-black/60 border ${estaVacia ? 'border-slate-700' : (esValida ? 'border-primary-cyan shadow-[0_0_15px_rgba(0,168,255,0.2)]' : 'border-danger-red/50')} text-white font-black text-lg rounded-xl py-4 px-4 focus:outline-none text-center tracking-widest uppercase transition-all duration-300`} 
                        placeholder={config.platePlaceholder} 
                      />
                    );
                  })()}
                  <p className="text-[9px] text-slate-500 font-bold mt-1 text-center uppercase tracking-tighter">{config.plateFormatHelp}</p>
                </div>

                {/* Especificaciones Técnicas */}
                <div className="grid grid-cols-2 gap-3 bg-[#1e293b]/40 p-4 rounded-xl border border-white/5">
                  <div className="col-span-2">
                    <label className="text-[13px] text-slate-400 font-bold uppercase tracking-wider block mb-1">Marca (Fabricante)</label>
                    <input type="text" value={nuevaMotoMarca} onChange={(e) => setNuevaMotoMarca(formatTitleCase(e.target.value))} className="w-full bg-black/40 border border-slate-700 text-white font-bold text-base rounded-lg py-3 px-3 focus:border-primary-cyan outline-none placeholder-slate-600" placeholder="Ej: Yamaha, Bajaj" />
                  </div>
                  <div>
                    <label className="text-[13px] text-slate-400 font-bold uppercase tracking-wider block mb-1">Línea / Modelo</label>
                    <input type="text" value={nuevaMotoModelo} onChange={(e) => setNuevaMotoModelo(formatTitleCase(e.target.value))} className="w-full bg-black/40 border border-slate-700 text-white font-bold text-base rounded-lg py-3 px-3 focus:border-primary-cyan outline-none placeholder-slate-600" placeholder="Ej: MT-09" />
                  </div>
                  <div>
                    <label className="text-[13px] text-slate-400 font-bold uppercase tracking-wider block mb-1">Cilindraje (CC)</label>
                    <input type="number" value={nuevaMotoCC} onChange={(e) => setNuevaMotoCC(e.target.value)} className="w-full bg-black/40 border border-slate-700 text-white font-bold text-base rounded-lg py-3 px-3 focus:border-primary-cyan outline-none placeholder-slate-600" placeholder="Ej: 890" />
                  </div>
                </div>

                {/* Documentos Legales */}
                <div className="bg-slate-900/80 p-4 mt-6 rounded-xl border border-gold-cert/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 bg-gold-cert/10 text-gold-cert text-[8px] font-black py-0.5 px-2 rounded-bl-lg uppercase tracking-widest border-b border-l border-gold-cert/20">Fechas Vencimiento</div>
                  <h4 className="text-gold-cert font-black text-xs uppercase tracking-widest mb-3 pt-2">Sincronización de Seguros</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-[13px] text-slate-400 font-bold uppercase block mb-1">Vencimiento SOAT</label>
                      <input type="date" value={nuevoSoat} onChange={(e) => setNuevoSoat(e.target.value)} className="w-full bg-black/60 border border-gold-cert/30 text-slate-200 font-bold text-base rounded-lg py-3 px-3 focus:border-gold-cert outline-none" />
                    </div>
                    <div>
                      <label className="text-[13px] text-slate-400 font-bold uppercase block mb-1">Vencimiento TecnoMecánica</label>
                      <input type="date" value={nuevaTecno} onChange={(e) => setNuevaTecno(e.target.value)} className="w-full bg-black/60 border border-gold-cert/30 text-slate-200 font-bold text-base rounded-lg py-3 px-3 focus:border-gold-cert outline-none" />
                    </div>
                    <div>
                      <label className="text-[13px] text-slate-400 font-bold uppercase block mb-1">Vencimiento Impuesto</label>
                      <input type="date" value={nuevoImpuesto} onChange={(e) => setNuevoImpuesto(e.target.value)} className="w-full bg-black/60 border border-gold-cert/30 text-slate-200 font-bold text-base rounded-lg py-3 px-3 focus:border-gold-cert outline-none" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Botón Guardar Inferior Fijo */}
              <div className="pt-2 pb-2 shrink-0">
                <button 
                  onClick={handleGuardarNuevaMoto}
                  disabled={(() => {
                    const limpia = nuevaMotoPlaca.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    const placaOk = config.plateRegEx.test(limpia);
                    return guardandoMoto || !placaOk || !nuevaMotoMarca || !nuevaMotoModelo || !nuevaMotoCC;
                  })()}
                  className="w-full py-4 bg-gradient-to-r from-primary-cyan to-blue-600 text-white rounded-xl font-black uppercase tracking-widest text-[14px] hover:opacity-90 active:scale-95 transition-all shadow-[0_0_20px_rgba(0,168,255,0.4)] disabled:opacity-30 disabled:grayscale disabled:shadow-none"
                >
                  {(() => {
                    const limpia = nuevaMotoPlaca.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    const placaOk = config.plateRegEx.test(limpia);
                    if (guardandoMoto) return "Escribiendo en Supabase...";
                    if (!placaOk && limpia.length > 0) return "Placa Inválida";
                    if (!nuevaMotoPlaca || !nuevaMotoMarca || !nuevaMotoModelo || !nuevaMotoCC) return "Completa Identidad";
                    return "Guardar en Bóveda Legal";
                  })()}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL GARANTÍAS/CITAS */}
      <AnimatePresence>
        {mostrarModalGarantia && (
          <div className="fixed inset-0 z-[100] flex flex-col justify-end">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={cerrarModalGarantia}
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`relative w-full ${themeClass} rounded-t-3xl p-6 shadow-[0_-10px_40px_rgba(0,168,255,0.2)] border-t border-primary-cyan/20 pb-8 max-h-[90vh] overflow-y-auto`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-1 rounded-full bg-white/20 mx-auto mb-6"></div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg text-white">
                  {editandoGarantiaId ? '🛠️ Editar Revisión' : '🛠️ Añadir Revisión o Cita'}
                </h3>
                <button 
                  onClick={cerrarModalGarantia} 
                  className="bg-slate-800 p-1.5 rounded-full text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex flex-col gap-3">
                 <p className="text-[14px] text-slate-400 font-medium leading-tight mb-4">
                  Programa mantenimientos, revisiones de kilometraje o garantías de fábrica.
                 </p>
                 
                 <label className="text-[13px] text-slate-400 font-bold mb-2 mt-2 block">Motivo o Mecánico</label>
                 <input 
                   type="text" 
                   value={nuevaGarantiaTitulo} 
                   onChange={(e) => setNuevaGarantiaTitulo(formatTitleCase(e.target.value))}
                   placeholder="Ej: Revisión 20.000 KM"
                   className="w-full bg-black/50 border border-white/10 text-white text-base font-bold rounded-lg py-3.5 px-3 mb-3 focus:border-primary-cyan outline-none" 
                 />

                 <label className="text-[13px] text-slate-400 font-bold mb-2 mt-2 block">Valor Estimado o Cotización ($)</label>
                 <input 
                   type="text" 
                   value={nuevaGarantiaValor} 
                   onChange={(e) => setNuevaGarantiaValor(formatMiles(e.target.value))}
                   placeholder="Ej: 80000"
                   className="w-full bg-black/50 border border-white/10 text-white text-base font-bold rounded-lg py-3.5 px-3 mb-3 focus:border-primary-cyan outline-none" 
                 />

                 <label className="text-[13px] text-slate-400 font-bold mb-2 mt-2 block">Fecha Programada</label>
                 <input 
                   type="date"
                   value={nuevaGarantiaFecha}
                   onChange={(e) => setNuevaGarantiaFecha(e.target.value)}
                   className="w-full bg-black/50 border border-white/10 text-slate-300 text-base font-bold rounded-lg py-3.5 px-3 mb-6 focus:border-primary-cyan outline-none" 
                 />
                 
                 <button 
                   onClick={handleGuardarGarantia}
                   className="w-full py-3.5 bg-blue-600 text-white rounded-lg font-black uppercase tracking-widest text-[14px] hover:bg-blue-500 active:scale-95 transition-all shadow-[0_0_15px_rgba(37,99,235,0.4)]"
                 >
                   {editandoGarantiaId ? 'Guardar Cambios' : 'Confirmar Agendamiento'}
                 </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL VENTAS PREMIUM ORO */}
      <AnimatePresence>
        {mostrarModalPremium && (
          <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/80 backdrop-blur-sm" onClick={() => setMostrarModalPremium(false)}>
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative w-full bg-gradient-to-b from-[#1e293b] to-black rounded-t-3xl p-6 shadow-[0_-10px_40px_rgba(251,191,36,0.15)] border-t border-gold-cert/30 pb-8 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-1 rounded-full bg-white/20 mx-auto mb-5"></div>
              
              <div className="flex justify-between items-start mb-6">
                <div></div>
                <button 
                  onClick={() => setMostrarModalPremium(false)} 
                  className="bg-slate-800 p-1.5 rounded-full text-slate-400 hover:text-white shrink-0 shadow-sm"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Botones Movidos Arriba */}
              <div className="flex flex-col gap-3 mb-8">
                 <button 
                   disabled={!isOro}
                   onClick={generarCertificadoOro}
                   className={`w-full py-3.5 rounded-xl font-black uppercase tracking-widest text-[12.5px] transition-all flex justify-center items-center gap-2 ${
                     isOro 
                     ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white shadow-[0_4px_20px_rgba(37,99,235,0.4)] hover:shadow-[0_4px_25px_rgba(37,99,235,0.6)] cursor-pointer' 
                     : 'bg-slate-800/50 text-slate-500 border border-white/5 shadow-inner cursor-not-allowed'
                   }`}
                 >
                   <span className="text-[15px] opacity-60">📜</span> Expedir Certificado
                 </button>

                 {!isOro && (
                   <button 
                     className="w-full py-4 bg-gradient-to-r from-gold-cert/90 to-yellow-600 text-yellow-950 rounded-xl font-black uppercase tracking-widest text-[14px] shadow-[0_4px_20px_rgba(251,191,36,0.4)] hover:shadow-[0_4px_25px_rgba(251,191,36,0.6)] active:scale-95 transition-all flex justify-center items-center gap-2"
                     onClick={() => {
                       setMostrarModalPremium(false);
                       if (onOpenMembresia) onOpenMembresia();
                     }}
                   >
                     <span className="text-[16px]">👑</span> Hazte ORO Ahora
                   </button>
                 )}
              </div>

              <div className="mb-4">
                <h3 className="font-black text-[22px] text-transparent bg-clip-text bg-gradient-to-r from-gold-cert to-yellow-600 leading-tight">
                  Beneficios del Certificado ORO
                </h3>
                <p className="text-[12px] text-slate-300 font-medium tracking-wide mt-1.5 leading-snug pr-4">
                  Blinda tu motocicleta y accede a herramientas exclusivas diseñadas para proteger tu inversión y tu vida en la vía.
                </p>
              </div>

              <div className="flex flex-col gap-4 mt-6">
                 
                 {/* Beneficio 1 */}
                 <div className="bg-slate-800/40 p-3.5 rounded-2xl border border-white/5 flex gap-3 items-start">
                   <div className="bg-danger-red/10 p-2 rounded-xl shrink-0 mt-0.5 shadow-md">
                     <span className="text-[16px]">🚨</span>
                   </div>
                   <div className="pt-0.5">
                     <h4 className="text-white font-bold text-[13px] tracking-wide mb-1">S.O.S Inteligente y Multi-Contacto</h4>
                     <p className="text-[11px] text-slate-400 leading-tight font-medium">Ante cualquier emergencia, con un solo botón envía tu ubicación en tiempo real a 3 moteros de tu red dentro de la app y a 2 contactos particulares de confianza directamente a su WhatsApp.</p>
                   </div>
                 </div>

                 {/* Beneficio 2 */}
                 <div className="bg-slate-800/40 p-3.5 rounded-2xl border border-white/5 flex gap-3 items-start">
                   <div className="bg-blue-500/10 p-2 rounded-xl shrink-0 mt-0.5 shadow-md">
                     <span className="text-[16px]">🏍️</span>
                   </div>
                   <div className="pt-0.5">
                     <h4 className="text-white font-bold text-[13px] tracking-wide mb-1">Cobertura Individual Exclusiva</h4>
                     <p className="text-[11px] text-slate-400 leading-tight font-medium">Guarda todas las motos que desees en tu Garaje de forma gratuita. La afiliación Premium y el Certificado ORO aplican de manera independiente por cada placa, brindándote seguridad individual y a la medida.</p>
                   </div>
                 </div>

                 {/* Beneficio 3 */}
                 <div className="bg-slate-800/40 p-3.5 rounded-2xl border border-white/5 flex gap-3 items-start">
                   <div className="bg-purple-500/10 p-2 rounded-xl shrink-0 mt-0.5 shadow-md">
                     <span className="text-[16px]">🔔</span>
                   </div>
                   <div className="pt-0.5">
                     <h4 className="text-white font-bold text-[13px] tracking-wide mb-1">Alertas Preventivas ORO</h4>
                     <p className="text-[11px] text-slate-400 leading-tight font-medium">Olvídate de las fechas. Recibirás notificaciones predictivas 15 días antes de que se te venzan tus pólizas o revisiones obligatorias, para que jamás pagues penalidades olvidadas.</p>
                   </div>
                 </div>

                 {/* Beneficio 4 */}
                 <div className="bg-slate-800/40 p-3.5 rounded-2xl border border-gold-cert/20 shadow-[0_0_15px_rgba(251,191,36,0.05)] flex gap-3 items-center">
                   <div className="bg-yellow-500/10 p-2 rounded-xl shrink-0 shadow-md">
                     <span className="text-[16px] drop-shadow-md">⭐</span>
                   </div>
                   <h4 className="text-gold-cert font-bold text-[13px] tracking-wide">Y muchos beneficios más dentro de la App.</h4>
                 </div>

              </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      {/* MODAL PREMIUM DE ÉXITO (SOLO PARA GASOLINA) */}
      <AnimatePresence>
        {toastGas.show && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[400] bg-black/80 backdrop-blur-xl flex items-center justify-center p-6"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className={`bg-[#0f172a] border ${toastGas.type === 'success' ? 'border-green-500/30' : 'border-primary-cyan/30'} rounded-[32px] p-8 w-full max-w-[340px] text-center shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col items-center`}
            >
               <div className={`w-20 h-20 ${toastGas.type === 'success' ? 'bg-green-500/10 border-green-500/20' : 'bg-primary-cyan/10 border-primary-cyan/20'} rounded-full flex items-center justify-center mb-6 border shadow-inner`}>
                  <div className={`w-14 h-14 ${toastGas.type === 'success' ? 'bg-green-500' : 'bg-primary-cyan'} rounded-full flex items-center justify-center animate-pulse`}>
                    {toastGas.type === 'success' ? <CheckCircle className="w-8 h-8 text-bg-dark" /> : <AlertCircle className="w-8 h-8 text-bg-dark" />}
                  </div>
               </div>
               
               <h3 className="text-white font-black text-xl uppercase tracking-widest mb-2">
                 {toastGas.type === 'success' ? '¡Sincronizado!' : 'Aviso Sistema'}
               </h3>
               <p className="text-slate-300 text-sm font-medium leading-relaxed mb-8 px-2">
                 {toastGas.msg}
               </p>

               <button 
                 onClick={() => setToastGas({ ...toastGas, show: false })}
                 className={`w-full ${toastGas.type === 'success' ? 'bg-green-500 hover:bg-green-400 text-green-950 shadow-[0_0_20px_rgba(34,197,94,0.4)]' : 'bg-primary-cyan hover:bg-cyan-400 text-bg-dark shadow-[0_0_20px_rgba(0,168,255,0.4)]'} font-black text-[13px] uppercase tracking-widest py-4 rounded-2xl transition-all active:scale-95`}
               >
                 Aceptar
               </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SISTEMA DE AVISOS PIKIS (VIP TOAST) */}
      <AnimatePresence>
        {toastPikis && (
          <motion.div 
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[200] w-[90%] max-w-sm"
          >
            <div className={`backdrop-blur-xl border flex items-center gap-3 p-4 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] ${toastPikis.tipo === 'success' ? 'bg-emerald-500/10 border-emerald-500/30' : toastPikis.tipo === 'error' ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-800/80 border-white/10'}`}>
               <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${toastPikis.tipo === 'success' ? 'bg-emerald-500 text-bg-dark' : toastPikis.tipo === 'error' ? 'bg-red-500 text-white' : 'bg-primary-cyan text-bg-dark'}`}>
                  {toastPikis.tipo === 'success' ? <CheckCircle className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
               </div>
               <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 leading-none mb-1">{toastPikis.tipo === 'success' ? 'Éxito' : toastPikis.tipo === 'error' ? 'Aviso de Error' : 'Información'}</span>
                  <p className="text-white text-[13px] font-bold leading-tight">{toastPikis.msg}</p>
               </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* DIÁLOGO DE CONFIRMACIÓN VIP (REEMPLAZO DE CONFIRM NATIVO) */}
      <AnimatePresence>
        {dialogoComp && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[250] bg-black/90 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-sm bg-slate-900 border border-primary-cyan/30 rounded-[40px] p-8 text-center shadow-[0_0_50px_rgba(0,168,255,0.2)]"
            >
              <div className="w-20 h-20 bg-primary-cyan/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary-cyan/20">
                <AlertCircle className="w-10 h-10 text-primary-cyan animate-pulse" />
              </div>
              <h3 className="text-white font-black text-xl mb-3 uppercase tracking-tighter">{dialogoComp.titulo}</h3>
              <p className="text-slate-400 text-[14px] font-medium leading-relaxed mb-8">{dialogoComp.msg}</p>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setDialogoComp(null)}
                  className="flex-1 py-4 rounded-2xl border border-white/10 text-white font-black text-[12px] uppercase tracking-widest hover:bg-white/5 transition-all"
                >
                  Cancelar
                </button>
                <button 
                  onClick={dialogoComp.accion}
                  className="flex-1 py-4 bg-primary-cyan text-bg-dark rounded-2xl font-black text-[12px] uppercase tracking-widest shadow-[0_0_20px_rgba(0,168,255,0.4)] hover:bg-cyan-400 active:scale-95 transition-all"
                >
                  ACEPTAR
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
}
